// ============================================================
// CaseListScreen.tsx
// Mobile-responsive case list screen
// ============================================================
import { refusalText } from '../../utils/serverError'
import { closeDateErrorForCases } from '../shared/caseCloseActions'
import { serverErrorMessage } from '../../utils/serverError'
import { canDeleteCases } from '../../utils/casePermissions'
import { BTN_PRIMARY_SM } from '../../styles/buttons'
import { ymdLocal } from '../tasks/myWorkLogic'
import React, { useEffect, useRef, useState } from 'react'
import { caseOpenErrorMessage, fetchCases, fetchCasesPaginated, deleteCaseApi, fetchCaseById, listWitnessesApi, listCaseDeadlinesApi, countCasesMissingLimitationApi, fetchCaseClaimTypeOptionsApi } from '../../api/casevoApi'
import type { RawCase, Witness } from '../../types/case.types'
import type { CaseDeadline } from '../../types/deadlines.types'
import { downloadCasesCsv } from '../../utils/exportCasesCsv'
import { describeDeleteCaseError, summariseBulkDeleteFailures } from '../../utils/deleteCaseError'
import CaseContextMenu from '../shared/CaseContextMenu'
import ExportCaseDialog, { type ExportFormat } from '../shared/ExportCaseDialog'
import DuplicateCaseDialog from '../shared/DuplicateCaseDialog'
import LinkCasePickerModal from '../shared/LinkCasePickerModal'
import ImportCasesModal from '../shared/ImportCasesModal'
import DialogueBox from '../shared/DialogueBox'
import ActionBox from '../shared/ActionBox'
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard'
import DateInput from '../shared/DateInput'
import { londonToday } from '../../utils/civilDate'
import { updateCaseApi, linkCaseApi } from '../../api/casevoApi'
import AudienceControl, { Audience, audienceFrom, audiencePayload } from '../shared/AudienceControl'
import { saveCaseAssignment, assignmentErrorMessage } from '../shared/caseAssignment'
import type { Pagination } from '../../types/auditLog.types'
import { useCasevoStore } from '../../store/useCasevoStore'
import { initialPlaceParam, navigateTo, usePlaceParam } from '../../navigation/navigate'
import { NO_LIMITATION_PARAM, NO_LIMITATION_VALUE, initialAssigneeFilterFor, initialStatusFilterFor, rowMissingLimitation } from '../../utils/missingLimitation'
import CaseStatusFilter from './CaseStatusFilter'
import CaseClaimTypeFilter from './CaseClaimTypeFilter'
import { CLAIM_TYPE_NONE, CLAIM_TYPE_PARAM, isClaimTypeFilterValue, type ClaimTypeFilterValue, type ClaimTypeOptions } from '../../utils/claimTypeFilter'
import { CLAIM_TYPE_SHORT_LABELS, type ClaimType } from '../../utils/claimTypes'
import CaseClaimTypeItem from './CaseClaimTypeItem'
import { caseStatusKey } from '../../utils/caseStatus'
import { Client } from '../../types/client.types'
import { formatDateUK, calculateCaseCompletion } from '../../utils/caseHelpers'
import ConfirmDialogue from '../shared/ConfirmDialogue'
import NewCaseModal, { type NewCaseTab } from '../cases/NewCaseModal'
import CaseQuickViewModal from '../shared/CaseQuickViewModal'
import { rawToSummary } from '../../utils/caseSummary'
import { caseName as caseDisplayName, caseClientName, caseNameWithRef, caseRef, NEW_CASE_NAME } from '../../utils/caseName'
import FavouriteStar from '../shared/FavouriteStar'
import AppHeader from './AppHeader'
import BackButton from './BackButton'
import { SidebarHamburger } from './Sidebar'
import SearchInput from '../shared/SearchInput'
import NewItemsIndicator from '../shared/NewItemsIndicator'
import { presence } from '../../services/presence'
import { useNewItemsCount } from '../../hooks/usePresence'
import { useFirmChange } from '../../utils/liveEvents'
import { setCaseBaseUpdatedAt } from '../../hooks/saveBaseVersion'
import { useLiveUserName } from '../../hooks/useLiveUserName'
export interface CaseSummary {
  id: string
  // Human-readable case title (nullable; null → row falls back to the
  // client name). Leads the row's hybrid label; client shown on sub-line.
  caseName?: string | null
  // Defendant name — an input to the shared caseName() rule ("Fiddler v Bell").
  // Returned by getCases so the card shows the final name from first paint.
  defendantName?: string | null
  // Live linked Client record (Phase 4). Preferred over the legacy
  // snapshot fields below; falls back to them for pre-Phase-4 rows
  // where client may be null.
  client?: Client | null
  clientForename: string
  clientSurname: string
  clientDateOfBirth: string | null
  clientNiNumber: string
  clientMainContactNumber: string
  clientEmailAddress: string
  accidentDate: string | null
  accidentLocation: string
  claimantReg: string
  openDate: string | null
  closeDate: string | null
  setupFinalised: boolean
  createdAt: string
  updatedAt: string
  // P4 backend column — section keys the user has marked complete.
  // Read-only here (mutations happen via useSectionCompletion which
  // fires its own PUT). Optional because rows pre-P4 may not have it
  // populated until they're touched.
  completedSections?: string[]
  // Stage A assignee. Nullable — the schema column is String? and
  // pre-Stage-A rows land null until admin (or a self-service claim)
  // sets one. The nested `assignedTo` join is populated by the
  // backend's SELECT include on getCases (caseController.ts L129-136);
  // no separate /api/users call needed to display the name.
  assignedToUserId: string | null
  assignedTo?: { id: string; firstName: string; lastName: string } | null
  // Audience (multi-assignee batch): firm-wide flag + handling-team userIds.
  firmWide?: boolean
  assignees?: { userId: string }[]
  // Any limitation-type deadline (open or completed)? From getCases — the
  // "No limitation date" flag + filter (utils/missingLimitation).
  hasLimitationDate?: boolean
  // The pinned limitation date ('YYYY-MM-DD') — the card's "Limitation" item.
  limitationDate?: string | null
  // (B2b) Confirmed "no time limit" (child sexual abuse, s.11ZA) — never
  // flagged; the card says "No time limit".
  noTimeLimit?: boolean
  // B2 claim type (null on older cases) — the card's short label.
  claimType?: ClaimType | null
}
// Status filter values — 'all' shows everything, others map to a single status
type StatusFilter = 'all' | 'draft' | 'open' | 'closed'
// Persisted-choice mirror of the type above. Same discipline as
// VALID_SORT_KEYS below: guard the localStorage read so a garbage
// value (manual edit, typo migration, a value from a future version
// the current build doesn't know about) falls back to 'all' rather
// than crashing the UI.
const VALID_STATUS_FILTERS: readonly StatusFilter[] = ['all', 'draft', 'open', 'closed']
// Exported so other views (e.g. DashboardView's clickable READY card) can
// preset this filter via localStorage BEFORE navigating here — this
// component lazy-reads the key on mount, so a write-then-navigate lands
// the list on the preset filter. Single source of truth for the key.
export const STATUS_FILTER_STORAGE_KEY = 'casevo_case_list_status_filter'
// Assignee filter values — the four semantic modes plus a passthrough
// for any specific firm-user UUID (admin-only per-user filter). Stored
// as a string in localStorage; parsed back into this discriminated
// space by the ASSIGNEE_FILTER_STORAGE_KEY reader.
type AssigneeFilter = 'all' | 'me' | 'unassigned' | string  // string = specific user UUID
// A link's one-shot assignee filter (the nudge email: ?assignee=me|all).
const ASSIGNEE_LINK_PARAM = 'assignee'
// Exported (same rationale as STATUS_FILTER_STORAGE_KEY) so DashboardView
// can preset assignee = 'me' before navigating to the READY-filtered list.
export const ASSIGNEE_FILTER_STORAGE_KEY = 'casevo_case_list_assignee_filter'
// Derived status (closed / open / draft) — the shared rule (utils/caseStatus),
// also used by the dashboard's "Open cases" tile.
const getStatusKey = (c: CaseSummary): Exclude<StatusFilter, 'all'> => caseStatusKey(c)
const isStatusFilter = (v: string | null): v is StatusFilter => !!v && (VALID_STATUS_FILTERS as readonly string[]).includes(v)
// Sort key values — persisted in localStorage so the choice survives reloads.
type SortKey = 'updatedAt' | 'name' | 'status'
const VALID_SORT_KEYS: readonly SortKey[] = ['updatedAt', 'name', 'status']
const SORT_STORAGE_KEY = 'casevo_case_list_sort'
// Explicit ordering for the "Status" sort — alphabetical would put Closed
// before Draft, which is wrong for case-management workflow.
const STATUS_SORT_ORDER: Record<Exclude<StatusFilter, 'all'>, number> = {
  draft: 0,
  open: 1,
  closed: 2,
}
// Defensive parse: missing/invalid timestamps coerce to 0 so they sort to
// the end of a "newest first" list rather than crashing on NaN comparisons.
const getUpdatedTime = (c: CaseSummary): number => {
  if (!c.updatedAt) return 0
  const t = new Date(c.updatedAt).getTime()
  return Number.isFinite(t) ? t : 0
}
// Date-search variants + the case search blob now live in utils/caseSearch.ts
// (shared with the Open-existing-case finder).
// Quick-view state: the row summary + any seed detail (full row + deadlines)
// so a Back re-open renders content on the first paint. initialFull/Deadlines
// are null on a fresh row-click (the modal fetches), populated on a Back re-open.
type QuickView = {
  summary: CaseSummary
  initialFull: RawCase | null
  initialDeadlines: CaseDeadline[] | null
}
// Module-scope cache of the LAST case sent to the full page from the quick
// modal, plus the detail the modal had already fetched. Survives the
// CaseListScreen unmount/remount that "Full case →" then Back triggers, so the
// lazy state initializer can re-seed the modal SYNCHRONOUSLY (no flash, no
// empty beat). Written only by "Full case →"; read only when a Back-restore's
// pendingQuickViewCaseId matches, so it never opens spuriously.
let quickViewReopenCache: { summary: CaseSummary; full: RawCase | null; deadlines: CaseDeadline[] | null } | null = null

// ── Wide-card line icons (Feather-style, currentColor) — no emojis. ──
const IcBase: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    className={className ?? 'w-3.5 h-3.5 shrink-0'} aria-hidden="true">{children}</svg>
)
const IcCalendar: React.FC<{ className?: string }> = (p) => <IcBase {...p}><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></IcBase>
const IcUser: React.FC<{ className?: string }> = (p) => <IcBase {...p}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></IcBase>
// Scales — the limitation date (as the case hero's Limitation fact).
const IcScale: React.FC<{ className?: string }> = (p) => <IcBase {...p}><path d="M12 3v18M7 21h10M5 7h14M7 7l-3 7a3 3 0 0 0 6 0L7 7zM17 7l-3 7a3 3 0 0 0 6 0l-3-7z" /></IcBase>
const IcClock: React.FC<{ className?: string }> = (p) => <IcBase {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></IcBase>
const IcTrash: React.FC<{ className?: string }> = (p) => <IcBase {...p}><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></IcBase>
const IcDots: React.FC<{ className?: string }> = (p) => <IcBase {...p}><circle cx="12" cy="5" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="19" r="1.5" /></IcBase>
const IcCheck: React.FC<{ className?: string }> = (p) => <IcBase {...p}><path d="M20 6L9 17l-5-5" /></IcBase>

const CaseListScreen: React.FC = () => {
  const { setFirmCaseCount } = useCasevoStore()
  // Auth-derived state for the assignee filter + row highlight.
  // authUser is the JWT payload cached in the store; its `id` is the
  // caller's own UUID (used to spot "my" cases in the row list) and
  // its `role` gates the admin-only per-user filter option.
  const authUser = useCasevoStore((s) => s.authUser)
  const currentUserId = authUser?.id ?? ''
  const isAdmin = authUser?.role === 'admin'
  const currentUserIdRef = useRef(currentUserId)
  currentUserIdRef.current = currentUserId
  // Quick-view re-open on Back: restoreView sets pendingQuickViewCaseId when a
  // caseList trail entry stamped with the modal is restored. Consume it (one-
  // shot) to re-open the modal for that case — so Back from a full case opened
  // FROM the modal retraces to the modal, not the bare list.
  const pendingQuickViewCaseId = useCasevoStore((s) => s.pendingQuickViewCaseId)
  const setPendingQuickViewCaseId = useCasevoStore((s) => s.setPendingQuickViewCaseId)
  // Logo click handler — also serves as the "go to case list" navigation
  // for other screens. Here it's effectively a no-op because we're already
  // on the list, but we still call the setters for consistency. Both are
  // value-equal idempotent so no re-render flash occurs.
  const handleLogoClick = () => {
    void navigateTo({ view: 'caseList' })
  }
  const [cases, setCases] = useState<CaseSummary[]>([])
  // Stage 4: "N new cases — refresh" indicator. Counts remote case CREATEs
  // (echo-suppressed; not the viewer's own). Click → loadCases() (respecting
  // current filters/sort/page) + reset. Never a blind insert.
  const [newCaseCount, resetNewCases] = useNewItemsCount('case')
  // Firm-wide live-freshness for the case list (Stages 2+3), server-driven via
  // the entity-changed channel. This is local state (not store-backed), so the
  // case rows are patched/removed HERE rather than in the central hook.
  //   • op:'update' → patch the matching row (caseName + status source fields)
  //     in place, and advance the concurrency base from `at` so this account's
  //     NEXT edit of the case doesn't spuriously 409 (preserving the base-
  //     advance the retired client-driven relay carried). EXISTING row only —
  //     a case outside the current filtered/paginated view is ignored, so we
  //     never inject an off-filter row (create live-insert is Stage 4).
  //   • op:'delete' → remove the row. (The "viewing the deleted case" edge is
  //     handled centrally in useEntityChangeSync.)
  // Echo-suppressed upstream (the actor already applied its own change). Server-
  // driven, so it also catches context-menu edits (rename / close-date) that
  // bypass the case-detail autosave the old client-driven relay relied on.
  // ★ AUDIENCE-AWARE live add / remove (multi-assignee batch). The receiver
  // runs inside a []-deps subscription, so it reads the CURRENT view state
  // (filters / page / Recent / loaded rows / loadCases) through refs that are
  // refreshed every render (assigned further down, once they're declared).
  const liveViewRef = useRef({
    recentOnly: false,
    isPaginating: false,
    page: 1,
    assigneeFilter: 'all' as string,
    seesAllCases: false,
  })
  const casesRef = useRef<CaseSummary[]>([])
  const loadCasesRef = useRef<() => void>(() => {})
  const liveReloadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    const unsubscribe = presence.subscribeEntityChanged((e) => {
      if (e.entity !== 'case') return
      if (e.op === 'delete') {
        setCases((prev) => prev.filter((c) => c.id !== e.id))
        return
      }
      if (e.op === 'update') {
        if (e.at) setCaseBaseUpdatedAt(e.id, e.at)
        const summary = (e.summary ?? {}) as Record<string, unknown>
        const rowPatch = e.at ? { ...summary, updatedAt: e.at } : summary
        const onList = casesRef.current.some((c) => c.id === e.id)

        // Audience-bearing event? (updateCase + updateCaseAssignee both carry
        // owner + team + firm-wide.) Anything else (older payloads) → patch-only,
        // exactly as before.
        const hasAudience = Array.isArray(summary.assignees) && 'assignedToUserId' in summary
        if (!hasAudience) {
          if (onList) {
            setCases((prev) => prev.map((c) => (c.id === e.id ? { ...c, ...(rowPatch as Partial<CaseSummary>) } : c)))
          }
          return
        }

        const v = liveViewRef.current
        const me = currentUserIdRef.current
        const owner = (summary.assignedToUserId as string | null) ?? null
        const teamIds = (summary.assignees as { userId: string }[]).map((a) => a.userId)
        const firmWide = summary.firmWide === true
        // Would the SERVER include this case in the viewer's current list?
        //  • visibility — same rule as getCases: admin / seeAllCases see all;
        //    else owner OR team member OR firm-wide;
        //  • the server-side assignee filter — 'me' / <uuid> = owner OR team
        //    (NOT firm-wide), matching getCases' ownerOrAssigneeOr; 'all' and
        //    'unassigned' (client-side) don't narrow here.
        const visible = v.seesAllCases || owner === me || teamIds.includes(me) || firmWide
        const filterTarget = v.assigneeFilter === 'all' || v.assigneeFilter === 'unassigned'
          ? null
          : (v.assigneeFilter === 'me' ? me : v.assigneeFilter)
        const matchesFilter = filterTarget === null || owner === filterTarget || teamIds.includes(filterTarget)
        const shouldBeListed = visible && matchesFilter

        if (onList) {
          // Still in the viewer's list → patch (as before). Fell OUT of it
          // (removed from the team / firm-wide off / owner changed away, and
          // not admin/see-all) → remove the row live.
          setCases((prev) => shouldBeListed
            ? prev.map((c) => (c.id === e.id ? { ...c, ...(rowPatch as Partial<CaseSummary>) } : c))
            : prev.filter((c) => c.id !== e.id))
          return
        }

        // Not on the list but now belongs in it → bring it in LIVE by silently
        // re-running the list's OWN fetch (debounced). This inserts it with the
        // exact server filter + sort + page semantics (getCases orders by
        // updatedAt desc, so a just-assigned case lands at the top of page 1)
        // and client-side status/search/sort apply as usual. Deliberately NOT
        // fetchCaseById — that endpoint records a case VIEW (CaseAccess +
        // access log), which would falsely mark the case as opened by this
        // user. Skipped when:
        //  • Recent is on — it lists cases *this user viewed* in 24h; a newly
        //    assigned case isn't one, so re-fetching wouldn't show it (the bell
        //    notification links straight to it instead);
        //  • paginating beyond page 1 — the case sorts to page 1;
        //  • the viewer's membership can't have changed (admin / see-all with
        //    no assignee filter → an off-list case is just on another page).
        const membershipCanChange = !v.seesAllCases || filterTarget !== null
        if (shouldBeListed && membershipCanChange && !v.recentOnly && (!v.isPaginating || v.page === 1)) {
          if (liveReloadTimerRef.current) clearTimeout(liveReloadTimerRef.current)
          liveReloadTimerRef.current = setTimeout(() => {
            liveReloadTimerRef.current = null
            loadCasesRef.current()
          }, 400)
        }
      }
    })
    return () => {
      unsubscribe()
      if (liveReloadTimerRef.current) { clearTimeout(liveReloadTimerRef.current); liveReloadTimerRef.current = null }
    }
  }, [])
  const [search, setSearch] = useState('')
  // ★ (Audit H6) The search runs on the SERVER (GET /api/cases?q=…, paged), so
  // typing is debounced: the list reloads 300 ms after the last keystroke.
  const SEARCH_DEBOUNCE_MS = 300
  const [debouncedSearch, setDebouncedSearch] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [search])
  // Status filter — same persistence discipline as sortKey below +
  // assigneeFilter further down: lazy-init reads localStorage on mount
  // (survives the CaseListScreen unmount/remount cycle that fires when
  // the user opens a case and hits Back), and the write-through effect
  // just below persists on change (survives a hard reload too).
  // Without this the plain useState('all') was re-initialising every
  // remount, quietly wiping the user's filter selection.
  // The URL wins (?view=cases&status=open — a refresh, Back/Forward or the
  // dashboard's "Open cases" tile), else the remembered choice.
  // ★ (Audit) Opened on "No limitation date" (the nudge / dashboard links) →
  // every status (utils/missingLimitation initialStatusFilterFor).
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(() => initialStatusFilterFor(
    { limitation: initialPlaceParam(NO_LIMITATION_PARAM), status: initialPlaceParam('status') },
    localStorage.getItem(STATUS_FILTER_STORAGE_KEY),
    isStatusFilter,
  ))
  // ★ (Audit) A status that arrived from a LINK (the dashboard / nudge /
  // ?status=) is not remembered — the user's own remembered choice survives
  // the visit. Remembered again from the user's first change.
  const linkStatusRef = useRef<StatusFilter | null>(
    initialPlaceParam(NO_LIMITATION_PARAM) === NO_LIMITATION_VALUE || isStatusFilter(initialPlaceParam('status')) ? statusFilter : null,
  )
  useEffect(() => {
    if (linkStatusRef.current !== null && statusFilter === linkStatusRef.current) return
    linkStatusRef.current = null
    localStorage.setItem(STATUS_FILTER_STORAGE_KEY, statusFilter)
  }, [statusFilter])
  // The status filter is part of the list's place (URL + history).
  usePlaceParam('status', statusFilter === 'all' ? null : statusFilter, (v) => setStatusFilter(isStatusFilter(v) ? v : 'all'))
  // ★ "No limitation date" filter — open (not closed) cases with no
  // limitation-type deadline (utils/missingLimitation; getCases'
  // hasLimitationDate). Part of the place only (?limitation=missing — the
  // dashboard's and the daily nudge's link), not remembered: it's a
  // safety check, not a preference. While on, the list spans EVERY case
  // (not just the last 24 hours) — filtered and paged on the server.
  const [noLimOnly, setNoLimOnly] = useState<boolean>(() => initialPlaceParam(NO_LIMITATION_PARAM) === NO_LIMITATION_VALUE)
  // The Status option's "No limitation date (N)" — GET
  // /api/cases/missing-limitation?count=1 for the list's assignee filter (the
  // number only; null = not loaded / failed → shown without a number).
  const [noLimCount, setNoLimCount] = useState<number | null>(null)
  usePlaceParam(NO_LIMITATION_PARAM, noLimOnly ? NO_LIMITATION_VALUE : null, (v) => setNoLimOnly(v === NO_LIMITATION_VALUE))
  // ★ (B2) Claim type filter — a type or "none" (not set); null = every type.
  // Part of the place (?claimType=…, utils/claimTypeFilter) like the
  // no-limitation filter; filtered and paged on the server. Its options (the
  // types present for the OTHER filters, with counts) come from
  // GET /api/cases/claim-types — null until loaded.
  const [claimTypeFilter, setClaimTypeFilter] = useState<ClaimTypeFilterValue | null>(() => {
    const v = initialPlaceParam(CLAIM_TYPE_PARAM)
    return isClaimTypeFilterValue(v) ? v : null
  })
  usePlaceParam(CLAIM_TYPE_PARAM, claimTypeFilter, (v) => setClaimTypeFilter(isClaimTypeFilterValue(v) ? v : null))
  const [claimTypeOptions, setClaimTypeOptions] = useState<ClaimTypeOptions | null>(null)
  // Recent-only filter — defaults to TRUE per solicitor spec: the case
  // list is "what I'm working on today", not a historical dump. The
  // toggle below the filter row flips it; the loadCases effect refetches
  // when it changes (backend's ?recent=24h drops the SQL filter entirely
  // when absent).
  //
  // Stage 4c: lifted from useState into the store slice
  // `caseListRecentOnly` (see useCasevoStore.ts). Two motivations:
  //   1) Survives CaseListScreen's remount when the user opens a case
  //      and hits Back — the local useState re-initialised to `true`
  //      every remount, defeating the trail-based Back-restore built
  //      in Stages 1-4b.
  //   2) The store slice write-throughs to localStorage
  //      ('casevo_case_list_recent_only'), mirroring the sortKey
  //      pattern below, so the tab also survives a full page reload.
  // All read sites and both setter calls below keep their local names
  // (recentOnly / setRecentOnly) — the swap is transparent to them.
  const recentOnly = useCasevoStore((s) => s.caseListRecentOnly)
  const setRecentOnly = useCasevoStore((s) => s.setCaseListRecentOnly)
  // Favourites cache — subscribed so the context menu's Favourite/Unfavourite
  // label reflects the live pinned state (and stays in sync with the row star).
  const favourites = useCasevoStore((s) => s.favourites)
  // Pagination state — Option A: only active when browsing (search empty
  // AND statusFilter 'all'). When searching/filtering we load ALL cases
  // so the client-side filter spans the whole set. pageChanging dims the
  // results list during a page transition so the user gets a visual
  // beat of feedback (mirrors AdminPage's AuditLogPanel).
  const PAGE_SIZE = 20
  const [page, setPage] = useState(1)
  const [pagination, setPagination] = useState<Pagination | null>(null)
  const [pageChanging, setPageChanging] = useState(false)
  // Sort key — read once from localStorage on mount via a lazy initializer.
  // Falls back to 'updatedAt' if the stored value is missing or unrecognised.
  const [sortKey, setSortKey] = useState<SortKey>(() => {
    const stored = localStorage.getItem(SORT_STORAGE_KEY)
    return stored && (VALID_SORT_KEYS as readonly string[]).includes(stored)
      ? (stored as SortKey)
      : 'updatedAt'
  })
  // Persist the user's choice immediately on change.
  useEffect(() => {
    localStorage.setItem(SORT_STORAGE_KEY, sortKey)
  }, [sortKey])
  // Assignee filter — 'all' | 'me' | 'unassigned' | <userUUID>.
  // Mirrors the sortKey pattern: lazy-init reads localStorage on
  // mount, write-through effect below persists on change. Chose this
  // pattern (over the store-slice pattern used by caseListRecentOnly)
  // because it's local to the component; adding a store slot would
  // couple the component's UI concerns to the global store for no
  // additional benefit — remount + reload survival come for free from
  // the localStorage read on init.
  //
  // The stored value is a plain string so it can be an assignee UUID
  // for admin's per-user picks. Non-admins can only round-trip
  // 'all' / 'me' / 'unassigned' through this cell — a stray UUID
  // written by an admin then loaded by a non-admin gracefully
  // degrades: the value is treated as unknown by the fetch (backend
  // would 403 if a non-admin passed a cross-user UUID), and the
  // display picker below only offers the three semantic options.
  const [assigneeFilter, setAssigneeFilter] = useState<AssigneeFilter>(() => {
    // ★ (Audit) A link may name the assignee filter (?assignee=me|all|… — the
    // daily nudge's email, so the list shows every case it counted); it wins
    // over the remembered one, once (consumed below).
    // Default (nothing remembered yet): your cases — owner or handling team.
    return initialAssigneeFilterFor(initialPlaceParam(ASSIGNEE_LINK_PARAM), localStorage.getItem(ASSIGNEE_FILTER_STORAGE_KEY))
  })
  // As the status: an assignee from a link isn't remembered.
  const linkAssigneeRef = useRef<string | null>(initialPlaceParam(ASSIGNEE_LINK_PARAM) ? assigneeFilter : null)
  useEffect(() => {
    if (linkAssigneeRef.current !== null && assigneeFilter === linkAssigneeRef.current) return
    linkAssigneeRef.current = null
    localStorage.setItem(ASSIGNEE_FILTER_STORAGE_KEY, assigneeFilter)
  }, [assigneeFilter])
  // The link's ?assignee= is one-shot: applied above, then dropped from the
  // place so a later refresh keeps the user's own choice.
  useEffect(() => {
    if (initialPlaceParam(ASSIGNEE_LINK_PARAM)) useCasevoStore.getState().setPlaceParam(ASSIGNEE_LINK_PARAM, null)
  }, [])
  // Firm-user cache for admin's per-user filter option. Loaded once
  // on mount for admins only — non-admins never see the per-user
  // option in the picker, so they don't need the list. Mirrors the
  // eager-mount fetch pattern in TasksView L119-128 (a select must
  // have its options ready when the user opens the dropdown, so
  // lazy-on-focus wouldn't show the per-user options in time).
  // PERF: read the SHARED firm-users slice (loaded once per session from the
  // same GET /api/users) instead of re-fetching the list on every visit.
  const firmUsers = useCasevoStore((s) => s.firmUsers)
  useEffect(() => {
    if (!isAdmin) return
    void useCasevoStore.getState().loadFirmUsers()
  }, [isAdmin])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [renameError, setRenameError] = useState<string | null>(null)
  const [assignError, setAssignError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  // CSV export state. Exporting fetches FULL data per case (the case-list
  // endpoint is trimmed), so it's async with its own loading + error.
  const [isExporting, setIsExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  // Per-row selection for "export selected". A Set of case ids. Empty = the
  // export falls back to "Export all".
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const toggleSelect = (id: string): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }
  // ── CONTEXT MENU ──────────────────────────────────────────
  // The open menu: viewport position + the case it targets. When the
  // targeted case IS in a multi-selection the menu shows the multi layout;
  // otherwise it's a single-case menu acting on that row.
  const [menu, setMenu] = useState<{ x: number; y: number; target: CaseSummary } | null>(null)
  // PDF export dialog for a right-clicked list case (needs FULL data — the
  // list rows are trimmed, so we fetch on demand).
  const [pdfExport, setPdfExport] = useState<
    { flat: Record<string, unknown>; witnesses: Witness[]; deadlines: CaseDeadline[]; format: ExportFormat } | null
  >(null)
  // Transient confirmation toast (e.g. "Reference copied").
  const [actionToast, setActionToast] = useState<string | null>(null)
  // Bulk-delete confirmation (the single-delete path uses deleteTarget).
  const [bulkDeleteIds, setBulkDeleteIds] = useState<string[] | null>(null)
  const firmName = useCasevoStore((s) => s.authUser?.firmName)
  // Status transitions. Status is DERIVED (closeDate → Closed; else
  // setupFinalised → Open; else Draft), so a change is a field write via
  // updateCaseApi. Close needs a date → its own dialog; bulk finalise/reopen
  // → a confirm.
  const [closeDialog, setCloseDialog] = useState<{ cases: CaseSummary[] } | null>(null)
  const [closeDateValue, setCloseDateValue] = useState<string | null>(null)
  // DATE HARDENING: Close waits while the date is half-typed or in the future.
  const [closeTyping, setCloseTyping] = useState(false)
  const [statusConfirm, setStatusConfirm] = useState<
    { kind: 'finalise' | 'reopen'; cases: CaseSummary[] } | null
  >(null)
  const [statusBusy, setStatusBusy] = useState(false)
  // "Assign to" dialog (right-click / ⋮). The SAME assignment model as the case
  // quick-view modal: the owner (fee earner, single FK via the assignee
  // endpoint) + the audience (Just me / Specific people / Everyone) via the
  // shared AudienceControl — saved through the shared saveCaseAssignment path.
  // ownerId: '__keep' = leave each case's owner as-is (multi-select, mixed).
  const [assignDialog, setAssignDialog] = useState<{
    cases: CaseSummary[]
    ownerId: string          // user id | '' (unassigned) | '__keep'
    audience: Audience
    audienceIds: string[]
    mixedAudience: boolean   // multi-select with differing teams
  } | null>(null)
  const [assignBusy, setAssignBusy] = useState(false)
  const canAssignCasesToOthers = authUser?.effectivePermissions?.assignCasesToOthers ?? false
  const canCreateFirmWideCases = authUser?.effectivePermissions?.createFirmWideCases ?? false
  // The dialog's people come from the SHARED firmUsers slice (the same source
  // the case quick-view modal uses) — GET /api/users is open to every member,
  // so a non-admin granted assignCasesToOthers gets a populated picker. (The
  // local admin-only `firmUsers` above is left untouched for its own uses.)
  const teamUsers = useCasevoStore((s) => s.firmUsers)
  // ★ Live names: render people by id against the central firmUsers/authUser
  // (patched on rename) — the row's assignedTo copy is only the fallback.
  const liveName = useLiveUserName()
  const loadTeamUsers = useCasevoStore((s) => s.loadFirmUsers)
  useEffect(() => { void loadTeamUsers() }, [loadTeamUsers])
  // Duplicate-case dialog (single case only).
  const [duplicateSource, setDuplicateSource] = useState<CaseSummary | null>(null)
  // Rename-case dialog (single case only). renameTarget holds the row being
  // renamed; renameValue is the editable text (pre-filled with the current
  // caseName, empty when unnamed). Empty on Save → clears the name (null).
  const [renameTarget, setRenameTarget] = useState<CaseSummary | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [renameBusy, setRenameBusy] = useState(false)
  // Unsaved-changes guard for the Rename dialog: a changed name not yet saved.
  const guardRename = useUnsavedChangesGuard(!!renameTarget && !renameBusy
    && renameValue.trim() !== (renameTarget.caseName ?? '').trim())
  const closeRename = (): void => guardRename(() => setRenameTarget(null))
  // Link-case picker (single case only). linkSource = the case being
  // linked FROM. The list doesn't know the case's existing links, so the
  // picker excludes only the source itself; the backend's dup-guard (409)
  // catches an already-linked pick and the picker surfaces it inline.
  const [linkSource, setLinkSource] = useState<CaseSummary | null>(null)
  const [linkBusy, setLinkBusy] = useState(false)
  const [linkError, setLinkError] = useState<string | null>(null)
  // CSV import (list-level). Opens the multi-step ImportCasesModal.
  const [importOpen, setImportOpen] = useState(false)

  useEffect(() => {
    if (!actionToast) return
    const t = window.setTimeout(() => setActionToast(null), 2500)
    return () => window.clearTimeout(t)
  }, [actionToast])

  // THE case reference (SURNAME-YEAR-ID) — shared caseRef() (utils/caseName.ts).
  const caseReference = (c: CaseSummary): string => caseRef(c)
  // New-case modal (case-entry-modals step 2) — driven by `newCaseTab`,
  // which doubles as open/closed flag and the client path it opens on:
  //   null       — closed
  //   'new'      — "+ New" / "+ Create your first case" (New client form)
  //   'existing' — open straight on the Existing-client tab (pick a client →
  //                new case for them). The "+ New" button opens on 'new'; the
  //                Existing-client tab is a switch inside the same modal.
  // Either way the modal creates the client (if new) + the case + the
  // optional case fields, then hands back the case id (handleCaseCreated).
  const [newCaseTab, setNewCaseTab] = useState<NewCaseTab | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<CaseSummary | null>(null)
  const [deleting, setDeleting] = useState(false)
  // Case-delete refusal / failure message (see utils/deleteCaseError.ts).
  const [deleteError, setDeleteError] = useState<string | null>(null)
  // Quick-view modal (case redesign Stage 1). Holds the row summary + any seed
  // detail (full row + deadlines) so a Back re-open shows content on the first
  // paint. Lazy initializer SYNCHRONOUSLY seeds from the module cache when this
  // mount is a Back-restore (pendingQuickViewCaseId set) — so the modal is open
  // over the list on the FIRST commit (no list-flash, no post-render effect).
  const [quickView, setQuickView] = useState<QuickView | null>(() => {
    const pid = useCasevoStore.getState().pendingQuickViewCaseId
    if (pid && quickViewReopenCache && quickViewReopenCache.summary.id === pid) {
      return {
        summary: quickViewReopenCache.summary,
        initialFull: quickViewReopenCache.full,
        initialDeadlines: quickViewReopenCache.deadlines,
      }
    }
    return null
  })
  // Consume the re-open signal one-shot: clear it, and (only if the lazy init
  // above DIDN'T already seed from cache) fall back to a loaded row or a fetch.
  useEffect(() => {
    if (!pendingQuickViewCaseId) return
    const id = pendingQuickViewCaseId
    setPendingQuickViewCaseId(null)
    // Already opened synchronously from cache → nothing to do (it refreshes on open).
    if (quickView && quickView.summary.id === id) return
    const found = cases.find((c) => c.id === id)
    if (found) { setQuickView({ summary: found, initialFull: null, initialDeadlines: null }); return }
    void fetchCaseById(id)
      .then((raw) => setQuickView({ summary: rawToSummary(raw), initialFull: raw, initialDeadlines: null }))
      .catch((err) => {
        // Blocked / deleted case → the shared clean message (toast) instead of
        // a silent failure.
        const msg = caseOpenErrorMessage(err)
        if (msg) { useCasevoStore.getState().setSaveConflict(msg); return }
        // eslint-disable-next-line no-console
        console.error('[CaseListScreen] Failed to re-open quick view for', id, err)
      })
    // One-shot consume keyed on the signal; deliberately not re-run on `cases`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingQuickViewCaseId])
  // Help-overlay state — opened by "?" keypress, closed by Esc / X / backdrop.
  const [showHelp, setShowHelp] = useState(false)
  // Focus the search input on mount so the user can start typing
  // immediately. Empty deps array → runs once; do NOT add deps that
  // change on keystroke or focus would be stolen mid-typing.
  const searchRef = useRef<HTMLInputElement>(null)
  // Dialog ref — receives focus when the overlay opens so screen
  // readers + keyboard users land inside the dialog.
  const dialogRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    searchRef.current?.focus()
  }, [])
  // Global keyboard shortcuts: "/" focuses the search input, "?" opens
  // the help overlay. Both bail when a modifier other than Shift is
  // held (Ctrl/Cmd/Alt may have OS-level meanings), when the user is
  // typing in an editable element, or when we're on a touch-primary
  // device with no useful keyboard.
  //
  // ? requires Shift on most keyboards (Shift+/). We deliberately do
  // NOT block on shiftKey — different layouts produce ? differently,
  // and the e.key === '?' string is what actually maps to user intent.
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (window.matchMedia?.('(pointer: coarse)').matches) return

    const isEditableTarget = (el: Element | null): boolean => {
      if (!el) return false
      const tag = el.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
      if ((el as HTMLElement).isContentEditable) return true
      return false
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return
      if (isEditableTarget(document.activeElement)) return
      if (e.key === '/') {
        e.preventDefault()
        searchRef.current?.focus()
      } else if (e.key === '?') {
        e.preventDefault()
        setShowHelp(true)
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])
  // When the overlay opens, take focus off the search input (so its
  // own onKeyDown can't intercept the closing Esc) and put focus into
  // the dialog. When the overlay closes (and only THEN — not on
  // initial mount), return focus to the search input.
  const prevShowHelpRef = useRef(showHelp)
  useEffect(() => {
    const wasOpen = prevShowHelpRef.current
    prevShowHelpRef.current = showHelp
    if (showHelp) {
      if (document.activeElement === searchRef.current) {
        searchRef.current?.blur()
      }
      dialogRef.current?.focus()
    } else if (wasOpen) {
      searchRef.current?.focus()
    }
  }, [showHelp])
  // Esc-to-close, attached only while the overlay is open. Using
  // capture phase + stopPropagation guarantees the search input's own
  // onKeyDown (which handles Esc to clear/blur) doesn't fire too —
  // belt-and-braces, since we already blurred the search above.
  useEffect(() => {
    if (!showHelp) return
    const onEsc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      e.stopPropagation()
      setShowHelp(false)
    }
    window.addEventListener('keydown', onEsc, { capture: true })
    return () => window.removeEventListener('keydown', onEsc, { capture: true })
  }, [showHelp])
  // ★ (Audit H6) ALWAYS paged: search, Status, "No limitation date" and
  // "Unassigned" all run on the server (GET /api/cases), so no view loads
  // every case any more. (The sort still orders the page shown.)
  const isPaginating = true
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadCases() }, [recentOnly, page, assigneeFilter, noLimOnly, statusFilter, debouncedSearch, claimTypeFilter])
  // ★ (Audit) "No limitation date (N)" follows deadline changes: a deadline
  // added / removed — by anyone (live event) or here (the store's deadlines,
  // e.g. the case open beside the list) — refreshes the count, and the list
  // too while it's showing that filter. Debounced.
  const deadlinesMap = useCasevoStore((s) => s.caseDeadlines)
  const deadlineRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scheduleDeadlineRefresh = useRef<() => void>(() => {})
  scheduleDeadlineRefresh.current = () => {
    if (deadlineRefreshTimerRef.current) clearTimeout(deadlineRefreshTimerRef.current)
    deadlineRefreshTimerRef.current = setTimeout(() => {
      deadlineRefreshTimerRef.current = null
      loadNoLimCount()
      if (noLimOnly) void loadCases()
    }, 600)
  }
  const deadlinesSeenRef = useRef(deadlinesMap)
  useEffect(() => {
    if (deadlinesSeenRef.current === deadlinesMap) return
    deadlinesSeenRef.current = deadlinesMap
    scheduleDeadlineRefresh.current()
  }, [deadlinesMap])
  useEffect(() => {
    const off = presence.subscribeEntityChanged((e) => { if (e.entity === 'deadline') scheduleDeadlineRefresh.current() })
    return () => { off(); if (deadlineRefreshTimerRef.current) clearTimeout(deadlineRefreshTimerRef.current) }
  }, [])
  // ★ Live permission change (e.g. seeAllCases granted/revoked): reload the
  // list so it matches what the server now lets this user see.
  const permissionsVersion = useCasevoStore((s) => s.permissionsVersion)
  const permVersionRef = useRef(permissionsVersion)
  useEffect(() => {
    if (permVersionRef.current === permissionsVersion) return
    permVersionRef.current = permissionsVersion
    void loadCases()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionsVersion])

  // ── CSV EXPORT ────────────────────────────────────────────
  // Download every firm case as a CSV (one row per case). The case-list
  // endpoint returns a TRIMMED projection, so we can't build a full export
  // from `cases` in memory — we fetch each case's FULL row via fetchCaseById.
  // NOTE: this is N requests (1 list + N detail). Acceptable for a v1 at a
  // modest case count; a bulk "all cases full" endpoint would be the scale-up.
  // Concurrency is capped so we don't fire hundreds of parallel requests.
  const EXPORT_CONCURRENCY = 6
  const fetchAllFull = async (ids: string[]): Promise<RawCase[]> => {
    const results: RawCase[] = new Array(ids.length)
    let cursor = 0
    const worker = async (): Promise<void> => {
      while (cursor < ids.length) {
        const idx = cursor++
        results[idx] = await fetchCaseById(ids[idx])
      }
    }
    await Promise.all(
      Array.from({ length: Math.min(EXPORT_CONCURRENCY, ids.length) }, worker),
    )
    return results
  }
  const handleExportCsv = async (): Promise<void> => {
    setExportError(null)
    setIsExporting(true)
    try {
      // If cases are ticked, export ONLY those ids (fewer requests, precise).
      // If none ticked, fall back to "export all" — fetch the (trimmed) firm
      // list just for its ids.
      let ids: string[]
      if (selectedIds.size > 0) {
        ids = Array.from(selectedIds)
      } else {
        const list = await fetchCases()
        ids = list.map((c) => c.id)
      }
      if (ids.length === 0) {
        setExportError('No cases to export.')
        return
      }
      const full = await fetchAllFull(ids)
      downloadCasesCsv(full)
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Failed to export cases. Please try again.')
    } finally {
      setIsExporting(false)
    }
  }

  // ★ (Audit) Request-id guards: each load / count takes a number, and only
  // the LATEST one may apply its response — a slow older request (an earlier
  // keystroke, the previous filter) can't overwrite a newer one.
  const loadSeqRef = useRef(0)
  const countSeqRef = useRef(0)
  const claimTypeSeqRef = useRef(0)
  // The Status dropdown's "No limitation date (N)" — the number only, for the
  // CURRENT assignee filter (the list's own server param, 'unassigned' too).
  // Best-effort: on a failure the option shows without a number.
  const loadNoLimCount = (): void => {
    const seq = ++countSeqRef.current
    countCasesMissingLimitationApi({ assignedToUserId: assigneeFilter === 'all' ? undefined : assigneeFilter, claimType: claimTypeFilter ?? undefined })
      .then((n) => { if (seq === countSeqRef.current) setNoLimCount(n) })
      .catch(() => { if (seq === countSeqRef.current) setNoLimCount(null) })
  }
  // The list's server filters other than the claim type and paging — shared
  // by the page load and the claim type options (so each option's count is
  // the rows that choice would show).
  const listFilterParams = () => ({
    // The no-limitation filter spans every case, not just recent ones.
    recent: recentOnly && !noLimOnly,
    // The assignee filter as the backend takes it: 'me' (the caller), a
    // user's id, or 'unassigned'; 'all' → no filter.
    assignedToUserId: assigneeFilter === 'all' ? undefined : assigneeFilter,
    q: debouncedSearch,
    statusKey: !noLimOnly && statusFilter !== 'all' ? statusFilter : undefined,
    limitation: noLimOnly ? 'missing' as const : undefined,
  })
  // The Claim type dropdown's options. Best-effort: on a failure the last
  // options stay (the dropdown never vanishes mid-use).
  const loadClaimTypeOptions = (): void => {
    const seq = ++claimTypeSeqRef.current
    fetchCaseClaimTypeOptionsApi(listFilterParams())
      .then((o) => { if (seq === claimTypeSeqRef.current) setClaimTypeOptions(o) })
      .catch(() => { /* keep the last options */ })
  }
  const loadCases = async () => {
    loadNoLimCount()
    loadClaimTypeOptions()
    const seq = ++loadSeqRef.current
    try {
      setError(null)
      // First load shows the skeletons (full setLoading); subsequent
      // page-transitions show the dim-and-keep-old-rows treatment.
      if (cases.length === 0) setLoading(true)
      else setPageChanging(true)
      const { data, pagination: pg } = await fetchCasesPaginated(page, PAGE_SIZE, {
        ...listFilterParams(),
        claimType: claimTypeFilter ?? undefined,
      })
      if (seq !== loadSeqRef.current) return // a newer load has started
      setCases(data)
      setPagination(pg)
      // Mirror the total into the store (not just the current page's length)
      // so the case-detail onboarding prompts still work — the firm total only
      // when no filter narrows it.
      if (!debouncedSearch && statusFilter === 'all' && !noLimOnly && assigneeFilter === 'all' && !claimTypeFilter) setFirmCaseCount(pg.total)
    } catch (err) {
      if (seq === loadSeqRef.current) setError('Failed to load cases.')
    } finally {
      if (seq === loadSeqRef.current) {
        setLoading(false)
        setPageChanging(false)
      }
    }
  }
  // Keep the live receiver's view of the list current (read at event time).
  liveViewRef.current = {
    recentOnly: recentOnly && !noLimOnly,
    isPaginating,
    page,
    assigneeFilter,
    seesAllCases: isAdmin || (authUser?.effectivePermissions?.seeAllCases ?? false),
  }
  casesRef.current = cases
  loadCasesRef.current = () => { void loadCases() }
  // (D12 live updates) Someone else created / changed / closed / deleted a
  // case (or its deadlines / client) → the list's own fetch, same filters.
  useFirmChange(['cases', 'clients'], () => { loadCasesRef.current() })
  // Reset to page 1 whenever the browse context changes. Guarded so it
  // doesn't fight the loadCases effect — only resets when not already on
  // page 1 (otherwise this would do nothing AND skip the no-op load).
  useEffect(() => {
    if (page !== 1) setPage(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, statusFilter, recentOnly, assigneeFilter, noLimOnly, claimTypeFilter])
  // Step 1a: open the client picker in CREATE mode. Both
  // case-creation buttons (header "+ New" and empty-state
  // "+ Create your first case") call this — they're the
  // create-a-new-client workflow. Actual case creation happens
  // AFTER the user fills the create form — see handleClientSelected.
  const handleNewCase = () => {
    setError(null)
    setNewCaseTab('new')
  }
  // Step 2: the New-case modal created the case (client created first if
  // new; optional case fields saved via updateCaseApi). Drop the user
  // straight into the new case's detail screen (same UX as before).
  // `warning` = the case exists but its optional fields didn't save.
  const handleCaseCreated = async (caseId: string, warning?: string) => {
    setNewCaseTab(null)
    try {
      setCreating(true)
      setFirmCaseCount(cases.length + 1)
      // One history step: Back returns to the list.
      await navigateTo({ view: 'caseDetail', caseId }, { intents: (s) => s.setCaseOpenedFrom('caseList') })
      if (warning) setError(warning)
    } catch (err) {
      setError('The case was created but couldn’t be opened. It’s in the case list.')
      void loadCases()
    } finally {
      setCreating(false)
    }
  }
  const handleOpenCase = async (id: string) => {
    try {
      // One history step: Back returns to the list (same tab).
      await navigateTo({ view: 'caseDetail', caseId: id }, { intents: (s) => s.setCaseOpenedFrom('caseList') })
    } catch (err) {
      setError('Failed to open case. Please try again.')
    }
  }
  const handleDeleteClick = (e: React.MouseEvent, c: CaseSummary) => {
    e.stopPropagation()
    setDeleteTarget(c)
  }
  // Used by the filtered-empty state's Clear filters button. Resets
  // the search box and the status dropdown but DELIBERATELY keeps the
  // sort dropdown — sort is a preference, not a filter. Refocuses the
  // search input so the keyboard user can immediately keep typing.
  const handleClearFilters = () => {
    setSearch('')
    setStatusFilter('all')
    // Also reset the assignee filter — a "Clear filters" that leaves
    // "Assigned to me" active would be misleading, since it still hides
    // firm-wide cases the user might expect to see back.
    setAssigneeFilter('all')
    setNoLimOnly(false)
    setClaimTypeFilter(null)
    setTimeout(() => searchRef.current?.focus(), 0)
  }
  // Page-step handlers — both bail out when there's no pagination state
  // (search/filter mode) or the destination is out of range. setPage's
  // function form keeps it correct under React 18's auto-batching.
  const handlePrevPage = () => { if (pagination && page > 1) setPage((p) => p - 1) }
  const handleNextPage = () => { if (pagination && page < pagination.totalPages) setPage((p) => p + 1) }
  const handleConfirmDelete = async () => {
    if (!deleteTarget) return
    setDeleteError(null)
    try {
      setDeleting(true)
      await deleteCaseApi(deleteTarget.id)
      setCases((prev) => {
        const next = prev.filter((c) => c.id !== deleteTarget.id)
        setFirmCaseCount(next.length)
        return next
      })
      // Success-only (the await above throws → catch on failure): the
      // backend cascade has removed any favourite pinning this case, so
      // re-fetch favourites in the background to drop the now-dead row
      // from the sidebar immediately. Fire-and-forget — doesn't block
      // the delete UX.
      void useCasevoStore.getState().loadFavourites()
      setDeleteTarget(null)
    } catch (err) {
      // A refusal (403) shows the SERVER's reason, e.g. "Only the case's
      // assignee or an admin can delete this case." — no "try again" (it
      // won't help). Network / 5xx keep the retry copy.
      setDeleteTarget(null)
      setDeleteError(describeDeleteCaseError(err).message)
    } finally {
      setDeleting(false)
    }
  }

  // ── CONTEXT-MENU ACTION HANDLERS ──────────────────────────
  // Open the menu at the cursor (right-click) or anchored to a rect (⋮ button).
  const openMenuAt = (x: number, y: number, target: CaseSummary): void => {
    setMenu({ x, y, target })
  }
  // Whether the menu targets a MULTI selection (target is part of a >1 set).
  const menuIsMulti = (target: CaseSummary): boolean =>
    selectedIds.has(target.id) && selectedIds.size > 1
  // Open the client-detail view for a case's client (store-driven nav).
  const openClientDetails = (c: CaseSummary): void => {
    const clientId = c.client?.id
    if (!clientId) return
    void navigateTo({ view: 'clientDetail', clientId })
  }
  // Copy the human reference to the clipboard + confirm via a transient toast.
  const copyReference = (c: CaseSummary): void => {
    const ref = caseReference(c)
    void navigator.clipboard?.writeText(ref).then(
      () => setActionToast(`Reference copied: ${ref}`),
      () => setActionToast('Could not copy reference'),
    )
  }
  // Export ONE list case — fetch full case + witnesses + deadlines (list data
  // is trimmed), then open the ONE Export case dialog (ExportCaseDialog) on the
  // chosen format: PDF options + print, or the CSV preview + download.
  // (Multi-select CSV stays a direct download — handleExportCsv.)
  const exportCsvSingle = (c: CaseSummary): Promise<void> => exportPdfFor(c, 'csv')
  const exportPdfFor = async (c: CaseSummary, format: ExportFormat = 'pdf'): Promise<void> => {
    setExportError(null)
    setIsExporting(true)
    try {
      const [full, wits, dls] = await Promise.all([
        fetchCaseById(c.id),
        listWitnessesApi(c.id),
        listCaseDeadlinesApi(c.id),
      ])
      setPdfExport({ flat: full as unknown as Record<string, unknown>, witnesses: wits, deadlines: dls, format })
    } catch {
      setExportError(format === 'csv' ? 'Failed to export case. Please try again.' : 'Failed to prepare PDF. Please try again.')
    } finally {
      setIsExporting(false)
    }
  }
  // Bulk delete the selected cases (menu multi "Delete N"). Confirmed via the
  // shared dialogue, then looped through deleteCaseApi.
  const confirmBulkDelete = async (): Promise<void> => {
    if (!bulkDeleteIds) return
    const ids = bulkDeleteIds
    setDeleteError(null)
    try {
      setDeleting(true)
      // allSettled: delete is owner/admin-only server-side, so some of a
      // mixed selection may be refused (403). Remove only the ones that
      // actually went, and say how many were refused.
      const results = await Promise.allSettled(ids.map((id) => deleteCaseApi(id)))
      const deleted = ids.filter((_, i) => results[i].status === 'fulfilled')
      const failures = results.flatMap((r) => (r.status === 'rejected' ? [describeDeleteCaseError(r.reason)] : []))
      setCases((prev) => {
        const next = prev.filter((c) => !deleted.includes(c.id))
        setFirmCaseCount(next.length)
        return next
      })
      setSelectedIds(new Set())
      void useCasevoStore.getState().loadFavourites()
      setBulkDeleteIds(null)
      // "N of M cases couldn't be deleted: <server reason>".
      const summary = summariseBulkDeleteFailures(ids.length, failures)
      if (summary) setDeleteError(summary)
    } catch {
      setDeleteError('Failed to delete the selected cases. Please try again.')
    } finally {
      setDeleting(false)
    }
  }

  // ── RENAME CASE ───────────────────────────────────────────
  // Save the case name via the existing case PUT (caseName is in
  // UPDATE_CASE_WHITELIST). Empty input → null (clears the name; the UI
  // falls back to the client name). On success, patch the row in-place so
  // the new label shows immediately — same setCases row-patch pattern as
  // applyStatus/applyAssignment, no re-fetch.
  const confirmRename = async (): Promise<void> => {
    if (!renameTarget) return
    setRenameError(null)
    const target = renameTarget
    const next = renameValue.trim() ? renameValue.trim() : null
    try {
      setRenameBusy(true)
      await updateCaseApi(target.id, { caseName: next })
      setCases((prev) => prev.map((row) => (row.id === target.id ? { ...row, caseName: next } : row)))
      // Propagate the rename to the sidebar surfaces (Recent list + the
      // Favourites cached label) and the open-case header — none of which
      // the local case-list `setCases` patch touches. displayName is the
      // hybrid: the new caseName, or the client name when the name's cleared.
      // Favourite label = THE "<name> · <REF>" (same builder the star uses).
      useCasevoStore.getState().applyCaseRename(target.id, next, caseNameWithRef({ ...target, caseName: next }))
      setActionToast(next ? 'Case renamed' : 'Case name cleared')
      setRenameTarget(null)
    } catch (err) {
      setRenameError(refusalText(err, 'Failed to rename the case. Please try again.'))
    } finally {
      setRenameBusy(false)
    }
  }

  // ── LINK CASE (from the list context menu) ────────────────
  // Firm-wide link the source case to the picked target via linkCaseApi.
  // On success toast + close; the backend guards self-link/dup/same-firm,
  // and the picker surfaces any error inline. If the linked/opened case
  // is currently open in the viewing pane, its links refresh next time
  // it's opened (getCaseById) — the list itself shows no link state.
  const confirmLink = async (targetCaseId: string): Promise<void> => {
    if (!linkSource) return
    setLinkBusy(true)
    setLinkError(null)
    try {
      await linkCaseApi(linkSource.id, targetCaseId)
      setActionToast('Case linked')
      setLinkSource(null)
    } catch (err) {
      setLinkError(err instanceof Error ? err.message : 'Failed to link case')
    } finally {
      setLinkBusy(false)
    }
  }

  // ── STATUS TRANSITIONS ────────────────────────────────────
  // Apply a field patch (setupFinalised / closeDate) to N cases via the
  // existing case PUT, then merge the patch into the list rows so getStatus
  // recomputes the chip (no re-fetch needed — the chip only reads closeDate +
  // setupFinalised). Mirrors the multi-delete Promise.all loop.
  const applyStatus = async (
    cases: CaseSummary[],
    patch: Partial<Pick<CaseSummary, 'setupFinalised' | 'closeDate'>>,
    toast: string,
  ): Promise<void> => {
    if (cases.length === 0) return
    // allSettled, not all: the cases that DID update are patched into the
    // list even when others are refused, and the refusal says why (the
    // server's own message, e.g. a close date before the open date).
    setStatusBusy(true)
    try {
      const results = await Promise.allSettled(cases.map((c) => updateCaseApi(c.id, patch)))
      const done = new Set(cases.filter((_, i) => results[i].status === 'fulfilled').map((c) => c.id))
      if (done.size) setCases((prev) => prev.map((row) => (done.has(row.id) ? { ...row, ...patch } : row)))
      const failedAt = results.map((r, i) => (r.status === 'rejected' ? i : -1)).filter((i) => i >= 0)
      if (failedAt.length === 0) { setActionToast(toast); return }
      const first = results[failedAt[0]] as PromiseRejectedResult
      const why = serverErrorMessage(first.reason) ?? 'Please try again.'
      const names = failedAt.slice(0, 3).map((i) => caseDisplayName(cases[i])).join(', ') + (failedAt.length > 3 ? '…' : '')
      setError(cases.length === 1 ? `Couldn’t update the case status: ${why}` : `${done.size} of ${cases.length} updated. Couldn’t update ${names}: ${why}`)
      if (done.size) setActionToast(`${plural(done.size, 'case')} updated`)
    } finally {
      setStatusBusy(false)
    }
  }
  const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`
  // Confirm handler for the bulk finalise/reopen dialogue.
  const confirmStatus = (): void => {
    if (!statusConfirm) return
    const { kind, cases } = statusConfirm
    setStatusConfirm(null)
    if (kind === 'finalise') void applyStatus(cases, { setupFinalised: true }, `Setup finalised for ${plural(cases.length, 'case')}`)
    else void applyStatus(cases, { closeDate: null }, `${plural(cases.length, 'case')} reopened`)
  }
  // The close date vs every selected case's open date (and the usual checks).
  const closeDialogError = closeDialog ? closeDateErrorForCases(closeDateValue, closeDialog.cases.map((c) => ({ openDate: c.openDate, label: caseDisplayName(c) })), londonToday()) : null
  // Confirm handler for the close-date dialogue.
  const confirmClose = (): void => {
    if (!closeDialog || !closeDateValue) return
    const cases = closeDialog.cases
    setCloseDialog(null)
    void applyStatus(cases, { closeDate: closeDateValue }, `${plural(cases.length, 'case')} closed`)
  }
  // Build the context-aware status menu items. SINGLE: one item for the
  // target's derived status. MULTI (mixed OK): one item per applicable subset —
  // each action applies only to the cases it validly fits.
  const statusItemsFor = (target: CaseSummary): { label: string; onClick: () => void }[] => {
    if (menuIsMulti(target)) {
      const selected = sortedCases.filter((c) => selectedIds.has(c.id))
      const drafts = selected.filter((c) => getStatusKey(c) === 'draft')
      const opens = selected.filter((c) => getStatusKey(c) === 'open')
      const closeds = selected.filter((c) => getStatusKey(c) === 'closed')
      const items: { label: string; onClick: () => void }[] = []
      if (drafts.length) items.push({ label: `Finalise setup (${drafts.length})`, onClick: () => setStatusConfirm({ kind: 'finalise', cases: drafts }) })
      if (opens.length) items.push({ label: `Close ${plural(opens.length, 'case')}…`, onClick: () => { setCloseDateValue(ymdLocal(new Date())); setCloseDialog({ cases: opens }) } })
      if (closeds.length) items.push({ label: `Reopen ${plural(closeds.length, 'case')}`, onClick: () => setStatusConfirm({ kind: 'reopen', cases: closeds }) })
      return items
    }
    // Single
    const s = getStatusKey(target)
    if (s === 'draft') return [{ label: 'Finalise setup', onClick: () => void applyStatus([target], { setupFinalised: true }, 'Setup finalised') }]
    if (s === 'open') return [{ label: 'Close case…', onClick: () => { setCloseDateValue(ymdLocal(new Date())); setCloseDialog({ cases: [target] }) } }]
    return [{ label: 'Reopen', onClick: () => void applyStatus([target], { closeDate: null }, 'Case reopened') }]
  }

  // ── ASSIGNMENT ────────────────────────────────────────────
  // Open the "Assign to" dialog pre-filled with the target case(s)' current
  // owner + audience. Multi-select: a shared value pre-fills; differing values
  // start as "keep owner" / "Just me" with a note that saving replaces them.
  const openAssignDialog = (cases: CaseSummary[]): void => {
    if (cases.length === 0) return
    const owners = new Set(cases.map((c) => c.assignedToUserId ?? ''))
    const keyOf = (c: CaseSummary): string =>
      `${c.firmWide ? 'F' : ''}|${(c.assignees ?? []).map((a) => a.userId).sort().join(',')}`
    const audienceKeys = new Set(cases.map(keyOf))
    const first = cases[0]
    const firstIds = (first.assignees ?? []).map((a) => a.userId)
    const shared = audienceKeys.size === 1
    setAssignDialog({
      cases,
      ownerId: owners.size === 1 ? Array.from(owners)[0] : '__keep',
      audience: shared ? audienceFrom(first.firmWide, firstIds) : 'me',
      audienceIds: shared ? firstIds : [],
      mixedAudience: !shared,
    })
  }

  // Save the dialog for N cases through the SHARED saveCaseAssignment path —
  // the exact calls the quick-view modal makes (owner → assignee endpoint;
  // audience → diff-gated case update, only the fields this user may change).
  // Then patch the list rows so the row updates without a re-fetch; other
  // users' lists update via the server's entity-changed broadcast.
  const applyAssignment = async (): Promise<void> => {
    if (!assignDialog) return
    const { cases, ownerId, audience, audienceIds } = assignDialog
    const payload = audiencePayload(audience, audienceIds, canAssignCasesToOthers, canCreateFirmWideCases)
    try {
      setAssignBusy(true)
      const results = await Promise.all(cases.map((c) =>
        saveCaseAssignment(c, { ownerId: ownerId === '__keep' ? undefined : (ownerId || null), audience: payload })
          .then((r) => [c.id, r] as const),
      ))
      const byId = new Map(results)
      setCases((prev) =>
        prev.map((row) => {
          const r = byId.get(row.id)
          if (!r) return row
          const u = r.assignedToUserId ? teamUsers.find((f) => f.id === r.assignedToUserId) : null
          return {
            ...row,
            assignedToUserId: r.assignedToUserId,
            assignedTo: r.assignedToUserId === row.assignedToUserId
              ? row.assignedTo
              : (u ? { id: u.id, firstName: u.firstName, lastName: u.lastName } : null),
            firmWide: r.firmWide,
            assignees: r.assigneeIds.map((userId) => ({ userId })),
          }
        }),
      )
      setActionToast(cases.length > 1 ? `${plural(cases.length, 'case')} updated` : 'Assignment updated')
      setAssignDialog(null)
    } catch (err) {
      // Said inside the window, which stays open.
      setAssignError(assignmentErrorMessage(err))
    } finally {
      setAssignBusy(false)
    }
  }
  // (The search runs on the server — backend lib/caseListSearch: client name,
  // case name / defendant, NI number, id / reference, accident date and date of
  // birth in the usual formats.)
  // The server applied the search, Status, "No limitation date" and
  // assignee filters (GET /api/cases, paged). Only a row that a LIVE patch
  // moved out of the current Status / no-limitation view is dropped here
  // until the next load.
  const filteredCases = cases.filter((c) => {
    if (!noLimOnly && statusFilter !== 'all' && getStatusKey(c) !== statusFilter) return false
    if (noLimOnly && !rowMissingLimitation(c)) return false
    if (claimTypeFilter && (c.claimType ?? CLAIM_TYPE_NONE) !== claimTypeFilter) return false
    return true
  })
  // A filter that can empty the list on its own (the "no matches" state, not
  // "No cases yet"). The server total (all pages) is what the counts show.
  const filterActive = !!debouncedSearch || statusFilter !== 'all' || noLimOnly || assigneeFilter === 'unassigned' || !!claimTypeFilter
  const shownTotal = pagination ? pagination.total : filteredCases.length
  // Sort runs after filtering. Spread first so we don't mutate filteredCases
  // (its .length still drives the count badge below).
  const sortedCases = [...filteredCases].sort((a, b) => {
    if (sortKey === 'name') {
      // Sort by the DISPLAYED case name (shared caseName() rule).
      const an = caseDisplayName(a).toLowerCase()
      const bn = caseDisplayName(b).toLowerCase()
      if (an !== bn) return an.localeCompare(bn)
      return getUpdatedTime(b) - getUpdatedTime(a) // tiebreaker: newest first
    }
    if (sortKey === 'status') {
      const sa = STATUS_SORT_ORDER[getStatusKey(a)]
      const sb = STATUS_SORT_ORDER[getStatusKey(b)]
      if (sa !== sb) return sa - sb
      return getUpdatedTime(b) - getUpdatedTime(a) // tiebreaker: newest first
    }
    // Default 'updatedAt' — newest first.
    return getUpdatedTime(b) - getUpdatedTime(a)
  })
  // Select-all operates on the CURRENTLY-SHOWN set (sortedCases = filtered +
  // searched + sorted), so ticking "select all" respects the active filter.
  const allShownSelected = sortedCases.length > 0 && sortedCases.every((c) => selectedIds.has(c.id))
  const someShownSelected = sortedCases.some((c) => selectedIds.has(c.id))
  const toggleSelectAll = (): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (sortedCases.length > 0 && sortedCases.every((c) => next.has(c.id))) {
        // All shown are selected → clear the shown ones.
        sortedCases.forEach((c) => next.delete(c.id))
      } else {
        // Some or none shown selected → select all shown.
        sortedCases.forEach((c) => next.add(c.id))
      }
      return next
    })
  }
  // Client (claimant) name — the card's sub-line + delete copy. The case's
  // own display name comes from the shared caseName() (utils/caseName.ts).
  const getClientName = (c: CaseSummary): string => caseClientName(c) || NEW_CASE_NAME
  const getStatus = (c: CaseSummary): { label: string; colour: string } => {
    if (c.closeDate) return { label: 'Closed', colour: 'bg-gray-100 dark:bg-casevo-border text-casevo-text-soft' }
    if (c.setupFinalised) return { label: 'Open', colour: 'bg-casevo-mint text-casevo-brand' }
    return { label: 'Draft', colour: 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-300' }
  }
  // Inline spinner — mirrors the LoginScreen pattern. `currentColor` lets
  // it inherit the button's text colour. Default size suits the empty-state
  // button; the smaller top-nav button overrides via the className prop.
  const Spinner: React.FC<{ className?: string }> = ({ className = 'h-4 w-4' }) => (
    <svg
      className={`animate-spin ${className}`}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4Z"
      />
    </svg>
  )
  // ── You / Firm grouping (view layer over the already filtered+sorted set) ──
  // Partition the final sortedCases into the caller's own cases and the rest;
  // order within each group is preserved from sortedCases, so sort/filter/
  // search/pagination all still apply. Rendered as two headed groups only when
  // BOTH are non-empty; otherwise a single flat spaced list (no lone heading).
  // "Mine" = I own it OR I'm on its handling team (CaseAssignee). Firm-wide
  // cases stay in the firm group — they're everyone's, not specifically mine.
  const isMineCase = (c: CaseSummary): boolean =>
    currentUserId !== '' &&
    (c.assignedToUserId === currentUserId || (c.assignees ?? []).some((a) => a.userId === currentUserId))
  const myCases = sortedCases.filter(isMineCase)
  const firmCases = sortedCases.filter((c) => !isMineCase(c))

  // One case card (the forest-gradient wide row). Extracted so both groups
  // render the identical card. Keyed by c.id for the .map.
  const renderCaseCard = (c: CaseSummary): React.ReactNode => {
    const status = getStatus(c)
    const completion = calculateCaseCompletion(c)
    const isMine = isMineCase(c)
    const assigneeDisplay = c.assignedTo
      ? liveName(c.assignedTo.id, `${c.assignedTo.firstName} ${c.assignedTo.lastName}`)
      : null
    const isSelected = selectedIds.has(c.id)
    return (
      // ★ WIDE CARD — forest-green gradient, white text, rounded, soft shadow +
      //   hover lift. Fixed forest gradient via LITERAL hex (NOT casevo-forest/
      //   brand tokens — those flip to light in dark mode and would invert the
      //   card), so it stays forest with white text in BOTH themes.
      <div
        key={c.id}
        className="group relative overflow-hidden rounded-[14px] text-white flex items-center gap-4 sm:gap-5 pl-5 sm:pl-6 pr-4 py-[18px] sm:py-5 shadow-[0_6px_18px_rgba(10,25,17,0.22)] dark:shadow-[0_2px_10px_rgba(0,0,0,0.3)] hover:shadow-[0_12px_30px_rgba(10,25,17,0.34)] dark:hover:shadow-[0_4px_14px_rgba(0,0,0,0.4)] hover:-translate-y-0.5 transition-all bg-gradient-to-br from-[#1a3a2a] to-[#2d6a4f] dark:from-[#1b2e24] dark:to-[#223c2e] dark:ring-1 dark:ring-inset dark:ring-white/[0.06]"
        onContextMenu={(e) => { e.preventDefault(); openMenuAt(e.clientX, e.clientY, c) }}
      >
        {/* Gold left bar — only for the current user's own cases. */}
        {isMine && <span aria-hidden="true" className="absolute left-0 top-0 bottom-0 w-[5px] bg-casevo-gold" />}
        {/* Multi-select checkbox (custom, ticks GOLD when selected). */}
        <button
          type="button"
          role="checkbox"
          aria-checked={isSelected}
          aria-label={`Select ${caseDisplayName(c)}`}
          onClick={(e) => { e.stopPropagation(); toggleSelect(c.id) }}
          className={`shrink-0 w-5 h-5 rounded-[5px] border flex items-center justify-center transition-colors ${isSelected ? 'bg-casevo-gold border-casevo-gold text-[#3a2e08]' : 'bg-white/10 border-white/50 text-transparent hover:bg-white/20'}`}
        >
          {isSelected && <IcCheck className="w-3 h-3" />}
        </button>
        {/* Open the quick-view modal (info + progress + status region). */}
        <button
          type="button"
          onClick={() => setQuickView({ summary: c, initialFull: null, initialDeadlines: null })}
          className="flex-1 min-w-0 flex items-center gap-3 sm:gap-4 text-left focus:outline-none casevo-focus"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5 mb-1">
              <span className="text-[18px] sm:text-[19px] font-bold leading-tight truncate">{caseDisplayName(c)}</span>
              {isMine && (
                <span className="shrink-0 inline-flex items-center gap-1 text-[11px] font-bold text-[#3a2e08] bg-casevo-gold px-2 py-0.5 rounded">
                  <IcCheck className="w-2.5 h-2.5" />You
                </span>
              )}
              {/* REF beside the name from 1100px; below that it moves to the
                  client line, so a long reference never squeezes the name out. */}
              <span className="hidden min-[1100px]:inline shrink-0 font-mono text-[12px] opacity-70">REF {caseRef(c)}</span>
            </div>
            <div className="text-[14px] opacity-85 truncate mb-2.5">
              {getClientName(c)}
              <span className="min-[1100px]:hidden font-mono text-[12px] opacity-70"> · REF {caseRef(c)}</span>
            </div>
            {/* ★ (Audit) Each item stays on one line (whitespace-nowrap); below
                1100px the Limitation item takes a second line of its own
                (basis-full) instead of wrapping part-way across the row. */}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[13px] opacity-85 whitespace-nowrap">
              {assigneeDisplay ? (
                <span className="inline-flex items-center gap-1.5 min-w-0 max-w-full"><IcUser /><span className="truncate">{assigneeDisplay}</span></span>
              ) : (
                <span className="inline-flex items-center gap-1.5 italic opacity-70"><IcUser />Unassigned</span>
              )}
              {c.accidentDate && <span className="inline-flex items-center gap-1.5"><IcCalendar />Accident {formatDateUK(c.accidentDate)}</span>}
              {/* (B2) Claim type — short label, same style as Accident; nothing when not set. */}
              <CaseClaimTypeItem claimType={c.claimType} />
              {/* ★ Limitation: the pinned limitation deadline (getCases'
                  limitationDate — open first, then earliest, as the hero and
                  Overview), or "Limitation not set" in amber on an open case
                  with none (utils/missingLimitation). Same style as Accident. */}
              {c.limitationDate ? (
                <span className="inline-flex items-center gap-1.5 basis-full min-[1100px]:basis-auto"><IcScale />Limitation {formatDateUK(c.limitationDate)}</span>
              ) : c.noTimeLimit && !c.limitationDate ? (
                <span className="inline-flex items-center gap-1.5 basis-full min-[1100px]:basis-auto" title="Confirmed: no time limit (Limitation Act s.11ZA)"><IcScale />No time limit</span>
              ) : rowMissingLimitation(c) ? (
                <span className="inline-flex items-center gap-1.5 basis-full min-[1100px]:basis-auto text-[#f6d77e]" title="No limitation date saved: nothing alerts until a “Limitation date” deadline is added"><IcScale />Limitation not set</span>
              ) : null}
              {/* Updated — from 1360px only: below that, with Limitation in the row, it would wrap onto a line of its own. */}
              <span className="hidden min-[1360px]:inline-flex items-center gap-1.5 opacity-70"><IcClock />Updated {formatDateUK(c.updatedAt)}</span>
            </div>
          </div>
          {/* Completion % bar — real progress = share of case-setup sections
              complete (calculateCaseCompletion). SWAP-POINT: when the stage
              engine lands, swap "Setup" for the stage name + drive by stage.
              From 1024px: below that it took the room the name and the
              details row need (the % is also in the quick view). */}
          <div className="hidden lg:block w-[160px] xl:w-[180px] shrink-0">
            <div className="flex justify-between text-[12px] opacity-80 mb-1.5">
              <span>Setup</span><span className="font-bold">{completion}%</span>
            </div>
            <div className="h-2 rounded bg-white/20 overflow-hidden">
              <div className="h-full rounded bg-gradient-to-r from-[#74c69d] to-[#a8e6c5] transition-all duration-300" style={{ width: `${completion}%` }} />
            </div>
          </div>
          {/* Status pill. */}
          <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide px-3 py-1 rounded bg-white/20">{status.label}</span>
        </button>
        {/* Right controls — favourite / delete / more (each stops propagation). */}
        <div className="shrink-0 flex items-center gap-0.5">
          <FavouriteStar
            itemType="case"
            itemId={c.id}
            label={caseNameWithRef(c)}
            variant="dark"
            className="!p-1.5"
          />
          {canDeleteCases(authUser, [c]) && (
          <button
            type="button"
            onClick={(e) => handleDeleteClick(e, c)}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/15 transition-colors"
            aria-label={`Delete ${caseDisplayName(c)}`}
            title={`Delete ${caseDisplayName(c)}`}
          >
            <IcTrash className="w-4 h-4" />
          </button>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              const r = (e.currentTarget as HTMLElement).getBoundingClientRect()
              openMenuAt(r.left, r.bottom, c)
            }}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/15 transition-colors"
            aria-label={`Actions for ${caseDisplayName(c)}`}
            title="Actions"
          >
            <IcDots className="w-4 h-4" />
          </button>
        </div>
      </div>
    )
  }

  // Group-heading classes — matching the mockup's gold treatment: the
  // "Assigned to you" heading is GOLD-BROWN on parchment (#8a6d18) and
  // flips to the green casevo-light in dark mode (mockup A vs C); "Firm
  // cases" is a muted slate that greys back in both themes.
  const groupHeadingBase = 'flex items-center gap-2 text-[12px] font-bold uppercase tracking-widest mb-4'
  const groupHeadingMineCls = `${groupHeadingBase} text-[#8a6d18] dark:text-casevo-light`
  const groupHeadingFirmCls = `${groupHeadingBase} text-[#6f7d73] dark:text-[#7d9488]`

  return (
    // ★ Caselist shell recolour — WARM PARCHMENT in light, DEEP-FOREST in dark
    //   (scoped to this screen; the app-wide Sidebar + AppHeader are already
    //   forest and left unchanged). Literal hex so it doesn't inherit the old
    //   minty offwhite; the dark: variant flips to the deep-forest ground.
    <div className="min-h-screen md:min-h-full bg-casevo-offwhite font-sans">
      {/* New case (case-entry-modals step 2) — ModalFrame-based. Opened by
          "+ New" (New client form; the Existing-client tab is inside). The
          modal creates client + case + optional case fields, then hands
          the case id to handleCaseCreated. */}
      <NewCaseModal
        isOpen={newCaseTab !== null}
        initialTab={newCaseTab ?? 'new'}
        onClose={() => setNewCaseTab(null)}
        onCreated={(caseId, warning) => { void handleCaseCreated(caseId, warning) }}
      />
      {/* Case quick-view (redesign Stage 1). Row click opens it; "Full case →"
          calls handleOpenCase (unchanged full-page open). onPatched syncs the
          list row after an in-modal edit — no re-fetch. */}
      {quickView && (
        <CaseQuickViewModal
          caseSummary={quickView.summary}
          initialFull={quickView.initialFull}
          initialDeadlines={quickView.initialDeadlines}
          onClose={() => setQuickView(null)}
          onOpenFull={(id, full, deadlines) => {
            // Cache the summary + already-fetched detail so a Back re-open
            // seeds the modal synchronously (no flash/empty beat).
            quickViewReopenCache = { summary: quickView.summary, full, deadlines }
            // STAMP the history entry being left with this case so Back
            // re-opens the quick modal (not the bare list). Read by the
            // snapshot navigateTo takes of the place being left (it clears it).
            setQuickView(null)
            // Pass the modal's already-fetched row so the full page skips the
            // getCaseById round-trip and paints its shell immediately (A).
            void navigateTo({ view: 'caseDetail', caseId: id }, {
              prefetched: full ?? undefined,
              intents: (s) => { s.setSnapshotQuickViewCaseId(id); s.setCaseOpenedFrom('quickView') },
            }).catch((err) => {
              // eslint-disable-next-line no-console
              console.error('[CaseListScreen] Failed to open full case from quick view:', err)
              setError('Failed to open case. Please try again.')
            })
          }}
          onOpenClient={(c) => { setQuickView(null); openClientDetails(c) }}
          onPatched={(id, patch) => setCases((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)))}
        />
      )}
      <ConfirmDialogue
        isOpen={!!deleteTarget}
        title="Delete case"
        message={`Are you sure you want to delete the case for ${deleteTarget ? getClientName(deleteTarget) : ''}? This cannot be undone.`}
        confirmLabel={deleting ? 'Deleting...' : 'Delete case'}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
      {/* Bulk delete (menu multi "Delete N") */}
      <ConfirmDialogue
        isOpen={!!bulkDeleteIds}
        title="Delete cases"
        message={`Delete ${bulkDeleteIds?.length ?? 0} selected case${(bulkDeleteIds?.length ?? 0) === 1 ? '' : 's'}? This cannot be undone.`}
        confirmLabel={deleting ? 'Deleting...' : `Delete ${bulkDeleteIds?.length ?? 0}`}
        onConfirm={confirmBulkDelete}
        onCancel={() => setBulkDeleteIds(null)}
      />
      {/* Bulk finalise / reopen confirmation */}
      <ConfirmDialogue
        isOpen={!!statusConfirm}
        title={statusConfirm?.kind === 'reopen' ? 'Reopen cases' : 'Finalise setup'}
        message={
          statusConfirm?.kind === 'reopen'
            ? `Reopen ${plural(statusConfirm?.cases.length ?? 0, 'case')}? This clears their close date.`
            : `Finalise setup for ${plural(statusConfirm?.cases.length ?? 0, 'case')}? They move to Open.`
        }
        confirmLabel={statusBusy ? 'Updating…' : 'Confirm'}
        onConfirm={confirmStatus}
        onCancel={() => setStatusConfirm(null)}
      />
      {/* Close-case date dialog */}
      <DialogueBox
        isOpen={!!closeDialog}
        title={closeDialog && closeDialog.cases.length > 1 ? `Close ${plural(closeDialog.cases.length, 'case')}` : 'Close case'}
        onClose={() => setCloseDialog(null)}
      >
        <div className="space-y-4">
          <div>
            <p className="block text-[12px] font-semibold text-casevo-forest mb-[5px]">Close date</p>
            <DateInput value={closeDateValue} onChange={setCloseDateValue} onValidityChange={(ok) => setCloseTyping(!ok)} ariaLabel="Close date" />
            {closeDialogError && <p className="mt-1 text-xs text-casevo-danger" role="alert">{closeDialogError}</p>}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setCloseDialog(null)}
              className="flex-1 inline-flex items-center justify-center text-sm font-medium rounded-lg px-5 py-2.5 border border-casevo-border text-casevo-mid hover:border-casevo-mid hover:text-casevo-forest transition-colors focus:outline-none casevo-focus disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmClose}
              disabled={!closeDateValue || closeTyping || !!closeDialogError || statusBusy}
              className="flex-1 inline-flex items-center justify-center gap-1.5 font-semibold text-white bg-casevo-primary hover:bg-casevo-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none casevo-focus text-sm rounded-lg px-5 py-2.5"
            >
              {statusBusy ? 'Closing…' : 'Close case'}
            </button>
          </div>
        </div>
      </DialogueBox>
      {/* Duplicate case */}
      {duplicateSource && (
        <DuplicateCaseDialog
          isOpen
          source={duplicateSource}
          onClose={() => setDuplicateSource(null)}
          onDuplicated={(msg) => setActionToast(msg)}
          onError={(msg) => setError(msg)}
        />
      )}
      {/* Link case (single case) — firm-wide picker. Excludes only the
          source itself; backend dup-guard (409) catches already-linked. */}
      {linkSource && (
        <LinkCasePickerModal
          isOpen
          onClose={() => setLinkSource(null)}
          currentCaseId={linkSource.id}
          excludeCaseIds={[]}
          onPick={(targetCaseId) => void confirmLink(targetCaseId)}
          busy={linkBusy}
          error={linkError}
        />
      )}
      {/* CSV importer (list-level). On completion, reload the case list so
          the newly-created cases appear. */}
      <ImportCasesModal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => { void loadCases() }}
      />
      {/* Rename case (single case). Text input pre-filled with the current
          name; empty on Save clears it (null → falls back to client name). */}
      <DialogueBox
        isOpen={!!renameTarget}
        title="Rename case"
        onClose={closeRename}
      >
        <div className="space-y-4">
          <div>
            <p className="block text-[12px] font-semibold text-casevo-forest mb-[5px]">Case name</p>
            <input
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !renameBusy) void confirmRename() }}
              placeholder="e.g. Smith v ABC Ltd"
              autoFocus
              maxLength={120}
              className="w-full border border-casevo-border rounded-lg px-3 py-2.5 text-sm bg-casevo-input dark:bg-[#1f2e26] text-casevo-forest placeholder:text-casevo-text-soft focus:outline-none focus:border-casevo-mid focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--casevo-mid)_18%,transparent)]"
            />
            <p className="text-xs text-casevo-text-soft mt-1.5">
              Leave empty to clear the name (falls back to the client name).
            </p>
            {renameError && <p role="alert" className="mt-2 mb-0 text-[12.5px] text-casevo-danger">{renameError}</p>}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={closeRename}
              className="flex-1 inline-flex items-center justify-center text-sm font-medium rounded-lg px-5 py-2.5 border border-casevo-border text-casevo-mid hover:border-casevo-mid hover:text-casevo-forest transition-colors focus:outline-none casevo-focus disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void confirmRename()}
              disabled={renameBusy}
              className="flex-1 inline-flex items-center justify-center gap-1.5 font-semibold text-white bg-casevo-primary hover:bg-casevo-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none casevo-focus text-sm rounded-lg px-5 py-2.5"
            >
              {renameBusy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </DialogueBox>
      {/* Assign to — owner + audience (the case quick-view's model). */}
      <DialogueBox
        isOpen={!!assignDialog}
        title={assignDialog && assignDialog.cases.length > 1 ? `Assign ${plural(assignDialog.cases.length, 'case')}` : 'Assign case'}
        onClose={() => { if (!assignBusy) { setAssignDialog(null); setAssignError(null) } }}
      >
        {assignError && <p role="alert" className="mt-0 mb-3 text-[12.5px] text-casevo-danger">{assignError}</p>}
        {assignDialog && (() => {
          // Owner options — unchanged rules: admins pick any active member;
          // non-admins may only self-assign or unassign (backend enforces).
          const selfOption = teamUsers.find((u) => u.id === currentUserId)
            ?? (authUser ? { id: authUser.id, firstName: authUser.firstName, lastName: authUser.lastName } : null)
          const ownerOptions: { id: string; firstName: string; lastName: string }[] = isAdmin
            ? teamUsers.filter((u) => u.isActive)
            : (selfOption ? [selfOption] : [])
          const lbl = 'block text-[11px] font-semibold uppercase tracking-wide text-casevo-mid mb-1.5'
          return (
            <div className="space-y-4">
              <div>
                <label className={lbl} htmlFor="assign-owner">Fee earner</label>
                <select
                  id="assign-owner"
                  value={assignDialog.ownerId}
                  disabled={assignBusy}
                  onChange={(e) => setAssignDialog((d) => (d ? { ...d, ownerId: e.target.value } : d))}
                  className="w-full border border-casevo-border rounded-lg px-3 py-2.5 text-sm bg-casevo-input dark:bg-[#1f2e26] text-casevo-forest focus:outline-none focus:border-casevo-mid disabled:opacity-60"
                >
                  {assignDialog.ownerId === '__keep' && <option value="__keep">Keep each case&apos;s current fee earner</option>}
                  {ownerOptions.map((u) => (
                    <option key={u.id} value={u.id}>{`${u.firstName} ${u.lastName}`.trim()}{u.id === currentUserId ? ' (you)' : ''}</option>
                  ))}
                  {/* Keep a current owner visible even if they're not in the non-admin option set. */}
                  {assignDialog.ownerId && assignDialog.ownerId !== '__keep' && !ownerOptions.some((u) => u.id === assignDialog.ownerId) && (() => {
                    const cur = teamUsers.find((u) => u.id === assignDialog.ownerId)
                    return <option value={assignDialog.ownerId} disabled>{cur ? `${cur.firstName} ${cur.lastName}`.trim() : 'Current fee earner'}</option>
                  })()}
                  <option value="">Unassigned</option>
                </select>
                {!isAdmin && <p className="text-[11px] text-casevo-mid mt-1">Only admins can assign a case to someone else.</p>}
              </div>
              <AudienceControl
                label="Assign to (handling team)"
                noun="case"
                audience={assignDialog.audience}
                onAudience={(a) => setAssignDialog((d) => (d ? { ...d, audience: a } : d))}
                selectedIds={assignDialog.audienceIds}
                onSelectedIds={(ids) => setAssignDialog((d) => (d ? { ...d, audienceIds: ids } : d))}
                canPeople={canAssignCasesToOthers}
                canFirm={canCreateFirmWideCases}
                disabled={assignBusy}
                firmUsers={teamUsers}
                currentUserId={currentUserId}
              />
              {assignDialog.mixedAudience && (canAssignCasesToOthers || canCreateFirmWideCases) && (
                <p className="text-[11px] text-casevo-mid -mt-2">These cases have different teams — saving applies this choice to all of them.</p>
              )}
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setAssignDialog(null)}
                  disabled={assignBusy}
                  className="flex-1 inline-flex items-center justify-center text-sm font-medium rounded-lg px-5 py-2.5 border border-casevo-border text-casevo-mid hover:border-casevo-mid hover:text-casevo-forest transition-colors focus:outline-none casevo-focus disabled:opacity-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void applyAssignment()}
                  disabled={assignBusy}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 font-semibold text-white bg-casevo-primary hover:bg-casevo-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none casevo-focus text-sm rounded-lg px-5 py-2.5"
                >
                  {assignBusy ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          )
        })()}
      </DialogueBox>
      {/* Case context menu (right-click / ⋮) */}
      {menu && (() => {
        const favourited = (favourites ?? []).some((f) => f.itemType === 'case' && f.itemId === menu.target.id)
        return (
        <CaseContextMenu
          x={menu.x}
          y={menu.y}
          selectedCount={menuIsMulti(menu.target) ? selectedIds.size : 1}
          canDelete={canDeleteCases(authUser, menuIsMulti(menu.target) ? cases.filter((c) => selectedIds.has(c.id)) : [menu.target])}
          canOpenClient={!!menu.target.client?.id}
          statusItems={statusItemsFor(menu.target)}
          isFavourited={favourited}
          target={{ title: caseDisplayName(menu.target), reference: caseRef(menu.target) }}
          onClose={() => setMenu(null)}
          handlers={{
            onOpen: () => void handleOpenCase(menu.target.id),
            onOpenNewTab: () => window.open(`${window.location.origin}/?caseId=${menu.target.id}`, '_blank'),
            onOpenClientDetails: () => openClientDetails(menu.target),
            onExportPdf: () => void exportPdfFor(menu.target),
            onExportCsv: () => {
              if (menuIsMulti(menu.target)) void handleExportCsv()
              else void exportCsvSingle(menu.target)
            },
            onCopyReference: () => copyReference(menu.target),
            onAssign: () => {
              const cases = menuIsMulti(menu.target)
                ? sortedCases.filter((c) => selectedIds.has(c.id))
                : [menu.target]
              openAssignDialog(cases)
            },
            onDuplicate: () => setDuplicateSource(menu.target),
            onRename: () => {
              setRenameValue(menu.target.caseName ?? '')
              setRenameTarget(menu.target)
            },
            onLink: () => {
              setLinkError(null)
              setLinkSource(menu.target)
            },
            onToggleFavourite: () => {
              const c = menu.target
              const label = caseNameWithRef(c)
              void useCasevoStore.getState().toggleFavourite({ itemType: 'case', itemId: c.id, label })
            },
            onDelete: () => {
              if (menuIsMulti(menu.target)) setBulkDeleteIds(Array.from(selectedIds))
              else setDeleteTarget(menu.target)
            },
            onClearSelection: () => setSelectedIds(new Set()),
          }}
        />
        )
      })()}
      {/* Per-case Export case dialog (PDF / CSV — opened from the list menu) */}
      {pdfExport && (
        <ExportCaseDialog
          isOpen
          initialFormat={pdfExport.format}
          onClose={() => setPdfExport(null)}
          caseFlat={pdfExport.flat}
          witnesses={pdfExport.witnesses}
          deadlines={pdfExport.deadlines}
          firmName={firmName}
        />
      )}
      {/* Transient action toast (e.g. reference copied) */}
      {actionToast && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[210] max-w-[90vw] px-4 py-2 rounded-lg shadow-lg border border-casevo-border bg-casevo-mint text-casevo-forest text-sm" role="status" aria-live="polite">
          {actionToast}
        </div>
      )}
      {/* Nav */}
      <AppHeader onWordmarkClick={handleLogoClick} hideSubtitleOnMobile leftHamburger={<SidebarHamburger />} leftSlot={<BackButton />}>
        {/* The "Demo2 User"-style name text used to live here as
            the only identity marker. Replaced by AppHeader's
            always-on right-edge <Avatar />, which is more
            visual + consistent across every screen. */}
        <button
          onClick={handleNewCase}
          disabled={creating}
          aria-busy={creating}
          className={BTN_PRIMARY_SM}
        >
          {creating && <Spinner className="h-3.5 w-3.5" />}
          {creating ? 'Creating...' : '+ New'}
        </button>
        {/* Import CSV — list-level bulk create (inverse of the CSV export).
            Outlined/secondary next to the primary "+ New". */}
        <button
          onClick={() => setImportOpen(true)}
          className="hidden sm:inline-flex text-xs uppercase tracking-widest text-casevo-light border border-casevo-light rounded px-3 py-2 sm:px-4 hover:text-white hover:border-white transition-colors"
        >
          Import
        </button>
      </AppHeader>
      {/* Content */}
      <div className="max-w-[1360px] mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-8">
        <div className="mb-5 sm:mb-6">
          <p className="text-xs uppercase tracking-widest text-[#9a7b1e] dark:text-casevo-light mb-1">Case Management</p>
          <div className="flex items-center gap-3">
            <h1 className="text-[22px] sm:text-[26px] font-medium tracking-[-0.01em] text-casevo-forest leading-[1.15]">All Cases</h1>
            {!loading && cases.length > 0 && (
              <span className="text-xs bg-[#efe4c4] text-[#8a6d18] dark:bg-[#26402f] dark:text-[#cbeed9] px-2 py-0.5 rounded-full">
                {shownTotal} {shownTotal === 1 ? 'case' : 'cases'}
              </span>
            )}
            {/* Right-aligned export controls: a select-all checkbox (respects
                the current filter/search — it toggles the SHOWN set) + the
                export button, whose label reflects the selection. */}
            <div className="ml-auto flex items-center gap-3">
              {!loading && cases.length > 0 && (
                <label className="inline-flex items-center gap-1.5 text-xs text-casevo-mid cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="h-4 w-4 shrink-0 cursor-pointer accent-casevo-brand"
                    checked={allShownSelected}
                    ref={(el) => { if (el) el.indeterminate = someShownSelected && !allShownSelected }}
                    onChange={toggleSelectAll}
                    aria-label="Select all shown cases"
                  />
                  Select all
                </label>
              )}
              {/* Export moved into the right-click / ⋮ context menu. This keeps
                  a light status: a selection count (a hint that right-click →
                  "Export as CSV (N)" acts on them) and an in-flight spinner. */}
              {selectedIds.size > 0 && !isExporting && (
                <span className="text-xs text-casevo-mid">{selectedIds.size} selected</span>
              )}
              {isExporting && (
                <span className="inline-flex items-center gap-1.5 text-xs text-casevo-mid">
                  <Spinner className="h-3.5 w-3.5" /> Exporting…
                </span>
              )}
            </div>
          </div>
          {exportError && (
            <p className="mt-2 text-xs text-red-600 dark:text-red-300">{exportError}</p>
          )}
        </div>
        {/* Stage 4: non-destructive "N new cases — refresh" banner. */}
        <NewItemsIndicator
          count={newCaseCount}
          noun="case"
          onRefresh={() => { resetNewCases(); void loadCases() }}
        />
        {/* Search + filters — stack on mobile; from sm the search takes its own
            line and the dropdowns wrap beneath it; all on one line from xl
            (five controls don't fit beside a usable search box before that). */}
        <div className="mb-5 sm:mb-6 flex flex-col sm:flex-row sm:flex-wrap xl:flex-nowrap gap-2 sm:gap-3">
          {/* Shared SearchInput — owns the input + clear-× affordance.
              flex-1 so it flexes beside the status/sort selects (xl+); the
              two-stage Esc handler is preserved via onKeyDown. */}
          <SearchInput
            value={search}
            onValueChange={setSearch}
            placeholder="Search by name, NI number, or case reference..."
            inputRef={searchRef}
            wrapperClassName="flex-1 sm:basis-full xl:basis-0"
            className="border border-casevo-border rounded-lg pl-4 py-3 text-sm bg-casevo-surface text-casevo-forest placeholder:text-casevo-text-soft focus:outline-none focus:border-casevo-mid"
            onKeyDown={(e) => {
              // Two-stage Esc — first press clears the query, second blurs.
              if (e.key !== 'Escape') return
              if (search.length > 0) {
                e.preventDefault()
                setSearch('')
              } else {
                e.preventDefault()
                searchRef.current?.blur()
              }
            }}
          />
          {/* Status — incl. "No limitation date (N)" below a divider when N > 0
              (that option = noLimOnly, kept in the URL as ?limitation=missing). */}
          <CaseStatusFilter
            value={noLimOnly ? 'nolimitation' : statusFilter}
            noLimitationCount={noLimCount}
            onChange={(v) => {
              if (v === 'nolimitation') { setNoLimOnly(true); setStatusFilter('all') }
              else { setNoLimOnly(false); setStatusFilter(v) }
            }}
            // ★ (Audit) While "No limitation date (N)" is the selected value it
            // sizes to fit it (never clipped); otherwise the usual 10rem, so the
            // search box keeps its room. (The open list is never clipped.)
            className={`${noLimOnly ? 'sm:w-auto sm:min-w-[10rem]' : 'sm:w-40'} shrink-0 border border-casevo-border rounded-lg px-3 py-3 text-sm bg-casevo-surface text-casevo-forest focus:outline-none focus:border-casevo-mid`}
          />
          {/* (B2) Claim type — only the types present (with counts), kept in
              the URL as ?claimType=…; hidden when there's nothing to filter. */}
          <CaseClaimTypeFilter
            value={claimTypeFilter}
            options={claimTypeOptions}
            onChange={setClaimTypeFilter}
            className="sm:w-40 shrink-0 border border-casevo-border rounded-lg px-3 py-3 text-sm bg-casevo-surface text-casevo-forest focus:outline-none focus:border-casevo-mid"
          />
          {/* Sort dropdown — paired visually with the status filter. */}
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as SortKey)}
            aria-label="Sort cases"
            className="sm:w-40 border border-casevo-border rounded-lg px-3 py-3 text-sm bg-casevo-surface text-casevo-forest focus:outline-none focus:border-casevo-mid"
          >
            <option value="updatedAt">Last updated</option>
            <option value="name">Name (A–Z)</option>
            <option value="status">Status</option>
          </select>
          {/* Assignee filter — orthogonal to All/Recent (recency is time-
              based, assignee is ownership-based; they compose via the same
              paged fetch). Same visual weight + Tailwind classes as
              the sort/status selects so the four controls read as a row.
              'me' resolves server-side to the caller's userId; 'unassigned'
              is a server filter too (no fee earner). Admins get
              an extra per-user picklist; non-admins never see the per-user
              options (both because they'd 403 on the backend for
              cross-user reads and because they don't need to). */}
          <select
            value={assigneeFilter}
            onChange={(e) => setAssigneeFilter(e.target.value)}
            aria-label="Filter by assignee"
            className="sm:w-40 border border-casevo-border rounded-lg px-3 py-3 text-sm bg-casevo-surface text-casevo-forest focus:outline-none focus:border-casevo-mid"
          >
            <option value="all">All assignees</option>
            <option value="me">Assigned to me</option>
            <option value="unassigned">Unassigned</option>
            {isAdmin && firmUsers.filter((u) => u.isActive && u.id !== currentUserId).length > 0 && (
              <optgroup label="Assigned to…">
                {firmUsers
                  .filter((u) => u.isActive && u.id !== currentUserId)
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {liveName(u.id, `${u.firstName} ${u.lastName}`)}
                    </option>
                  ))}
              </optgroup>
            )}
          </select>
        </div>
        {/* Recent-only toggle — persistent above all the conditional
            states (loading, empty, results) so the user can always
            flip between "what I'm working on today" and the full
            firm-wide list. Subtle inline link styling so it doesn't
            compete with the search + filter row above. */}
        <div className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-xs text-casevo-mid">
          <span>Showing {noLimOnly ? 'all open cases with no limitation date' : recentOnly ? 'last 24 hours' : 'all cases'}</span>
          {!noLimOnly && <button
            type="button"
            onClick={() => setRecentOnly((v) => !v)}
            className="text-[#8a6d18] dark:text-casevo-light underline hover:opacity-70 transition-opacity"
          >
            {recentOnly ? 'Show all cases' : 'Show last 24 hours'}
          </button>}
        </div>
        {/* Delete refusal / failure — its OWN banner (not the load-error one,
            whose "Try again" reloads the list and would imply a retry helps).
            Dismissible; the list stays visible underneath. */}
        {deleteError && (
          <ActionBox alert tone="danger" className="mb-4" message={deleteError}
            actions={[{ label: 'Dismiss', kind: 'link', onClick: () => setDeleteError(null) }]} />
        )}
        {error && (
          <ActionBox alert tone="danger" className="mb-4" message={error}
            actions={[{ label: 'Try again', kind: 'link', onClick: () => { void loadCases() } }]} />
        )}
        {loading && (
          // NEUTRAL loading skeleton — mirrors the NEW forest card's SIZE +
          // horizontal LAYOUT (same rounded-[14px], padding, gap-4 list, and
          // the checkbox → info → progress → status → controls row structure)
          // so there's no jump when the real cards land. Deliberately NOT
          // forest/gold: calm faint-grey placeholder blocks on the
          // parchment/deep-forest ground (a green skeleton reads heavy/odd).
          // Card base: black/[0.04] on parchment, white/[0.05] on deep-forest;
          // placeholder bars a shade stronger (black/10 / white/10) so shapes read.
          <div className="flex flex-col gap-4">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="rounded-[14px] bg-black/[0.04] dark:bg-white/[0.05] flex items-center gap-4 sm:gap-5 pl-5 sm:pl-6 pr-4 py-[18px] sm:py-5 animate-pulse"
              >
                {/* Checkbox spot */}
                <div className="shrink-0 w-5 h-5 rounded-[5px] bg-black/10 dark:bg-white/10" />
                {/* Info block — name / client / meta lines */}
                <div className="flex-1 min-w-0">
                  <div className="w-40 sm:w-52 h-[18px] rounded bg-black/10 dark:bg-white/10 mb-2.5" />
                  <div className="w-28 sm:w-36 h-3.5 rounded bg-black/10 dark:bg-white/10 mb-2.5" />
                  <div className="w-48 sm:w-64 h-3 rounded bg-black/10 dark:bg-white/10" />
                </div>
                {/* Progress column — matches the card: from 1024px, w-[160px] xl:w-[180px] + h-2 bar */}
                <div className="hidden lg:block w-[160px] xl:w-[180px] shrink-0">
                  <div className="w-16 h-3 rounded bg-black/10 dark:bg-white/10 mb-1.5" />
                  <div className="h-2 rounded bg-black/10 dark:bg-white/10" />
                </div>
                {/* Status pill spot */}
                <div className="shrink-0 w-16 h-6 rounded bg-black/10 dark:bg-white/10" />
                {/* Controls area — three 8×8 button spots */}
                <div className="shrink-0 flex items-center gap-0.5">
                  <div className="w-8 h-8 rounded-lg bg-black/10 dark:bg-white/10" />
                  <div className="w-8 h-8 rounded-lg bg-black/10 dark:bg-white/10" />
                  <div className="w-8 h-8 rounded-lg bg-black/10 dark:bg-white/10" />
                </div>
              </div>
            ))}
          </div>
        )}
        {/*
          First-time empty state — only fires when the firm genuinely has zero
          cases. Distinct from the filtered-empty state below (which fires when
          cases exist but the search/status filter hides them all).
        */}
        {!loading && !error && cases.length === 0 && !filterActive && (
          <div className="flex flex-col items-center justify-center text-center px-4 py-16 sm:py-24 min-h-[60vh]">
            {/* Folder icon — Casevo mint fill with brand stroke for a soft, on-theme look */}
            <svg
              width="72"
              height="72"
              viewBox="0 0 72 72"
              fill="none"
              aria-hidden="true"
              className="mb-6 text-casevo-brand opacity-60"
            >
              <path
                d="M10 22a4 4 0 0 1 4-4h14l4 5h26a4 4 0 0 1 4 4v25a4 4 0 0 1-4 4H14a4 4 0 0 1-4-4V22Z"
                className="fill-casevo-mint"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinejoin="round"
              />
              <path
                d="M22 36h22M22 44h14"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
            </svg>
            {/* Two empty-state variants share the same outer container
                + icon. The recent-filter case shows different copy and
                routes the user back to the full directory rather than
                pushing them to create a new case (they may already have
                plenty — just nothing viewed recently). */}
            {recentOnly ? (
              <>
                <h2 className="text-casevo-forest font-medium text-xl sm:text-2xl mb-3">
                  No recent cases
                </h2>
                <p className="text-casevo-mid text-sm sm:text-base max-w-md mb-8 leading-relaxed">
                  You haven't viewed any cases in the last 24 hours.
                  Show all cases to see your full directory.
                </p>
                <button
                  onClick={() => setRecentOnly(false)}
                  className="bg-casevo-primary text-white text-sm px-6 py-3 rounded-lg hover:bg-casevo-primary-hover transition-colors uppercase tracking-widest font-medium inline-flex items-center justify-center gap-2"
                >
                  Show all cases
                </button>
              </>
            ) : (
              <>
                <h2 className="text-casevo-forest font-medium text-xl sm:text-2xl mb-3">
                  No cases yet
                </h2>
                <p className="text-casevo-mid text-sm sm:text-base max-w-md mb-8 leading-relaxed">
                  Get started by creating your first case. You can track client
                  details, accident information, and progress all in one place.
                </p>
                <button
                  onClick={handleNewCase}
                  disabled={creating}
                  aria-busy={creating}
                  className="bg-casevo-primary text-white text-sm px-6 py-3 rounded-lg hover:bg-casevo-primary-hover transition-colors uppercase tracking-widest disabled:opacity-50 font-medium inline-flex items-center justify-center gap-2"
                >
                  {creating && <Spinner />}
                  {creating ? 'Creating...' : '+ Create your first case'}
                </button>
              </>
            )}
          </div>
        )}
        {!loading && !error && filteredCases.length === 0 && (cases.length > 0 || filterActive) && (() => {
          // Build the dynamic subtitle. The four branches below cover
          // every combination of (search active) × (status active);
          // the all-clear case is defensive — the empty state can't
          // actually fire when both filters are off.
          const displayQuery = search.trim()
          const statusLabel =
            statusFilter === 'all'
              ? ''
              : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)
          let subtitle: string
          if (displayQuery && statusFilter !== 'all') {
            subtitle = `No cases match the search '${displayQuery}' with status '${statusLabel}'`
          } else if (displayQuery) {
            subtitle = `No cases match the search '${displayQuery}'`
          } else if (statusFilter !== 'all') {
            subtitle = `No cases match the status '${statusLabel}'`
          } else if (claimTypeFilter) {
            const typeLabel = claimTypeFilter === CLAIM_TYPE_NONE ? 'Not set' : CLAIM_TYPE_SHORT_LABELS[claimTypeFilter]
            subtitle = noLimOnly ? `Every open case with claim type '${typeLabel}' has a limitation date` : `No cases match the claim type '${typeLabel}'`
          } else if (noLimOnly) {
            subtitle = 'Every open case has a limitation date'
          } else {
            subtitle = 'No cases match your filters'
          }
          return (
            <div className="flex flex-col items-center justify-center text-center px-4 py-16 sm:py-24 min-h-[60vh]">
              {/* Magnifying glass with strikethrough — same visual weight
                  as the first-time folder icon, mint fill + brand stroke. */}
              <svg
                width="72"
                height="72"
                viewBox="0 0 72 72"
                fill="none"
                aria-hidden="true"
                className="mb-6 text-casevo-brand opacity-60"
              >
                <circle
                  cx="30"
                  cy="30"
                  r="18"
                  className="fill-casevo-mint"
                  stroke="currentColor"
                  strokeWidth="3"
                />
                <line
                  x1="44"
                  y1="44"
                  x2="60"
                  y2="60"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
                <line
                  x1="18"
                  y1="42"
                  x2="42"
                  y2="18"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
              <h2 className="text-casevo-forest font-medium text-xl sm:text-2xl mb-3">
                No cases match
              </h2>
              <p className="text-casevo-mid text-sm sm:text-base italic max-w-md mb-8 leading-relaxed">
                {subtitle}
              </p>
              <button
                onClick={handleClearFilters}
                className="bg-casevo-primary text-white text-sm px-6 py-3 rounded-lg hover:bg-casevo-primary-hover transition-colors uppercase tracking-widest font-medium"
              >
                Clear filters
              </button>
            </div>
          )
        })()}
        {!loading && filteredCases.length > 0 && (
          <div className={`${pageChanging ? 'opacity-60' : ''} transition-opacity`}>
            <p className="text-xs text-casevo-mid uppercase tracking-widest mb-4">
              {shownTotal} {shownTotal === 1 ? 'case' : 'cases'}
              {debouncedSearch && ` matching "${debouncedSearch}"`}
            </p>
            {/* You / Firm grouping — only when BOTH buckets have cases; else a
                single flat, generously-spaced list (no lone heading). Cards are
                rendered by renderCaseCard so both groups are identical. */}
            {myCases.length > 0 && firmCases.length > 0 ? (
              <>
                <h2 className={groupHeadingMineCls}>
                  Assigned to you <span className="text-casevo-mid font-semibold normal-case tracking-normal">· {myCases.length}</span>
                </h2>
                <div className="flex flex-col gap-4 mb-9">{myCases.map(renderCaseCard)}</div>
                <h2 className={groupHeadingFirmCls}>
                  Firm cases <span className="text-casevo-mid font-semibold normal-case tracking-normal">· {firmCases.length}</span>
                </h2>
                <div className="flex flex-col gap-4">{firmCases.map(renderCaseCard)}</div>
              </>
            ) : (
              <div className="flex flex-col gap-4">{sortedCases.map(renderCaseCard)}</div>
            )}
          </div>
        )}
        {/* Pager footer — only renders in browse mode (pagination !== null).
            When the user is searching or filtering we've already loaded the
            whole set, so a page footer would be misleading. Same prev/next
            recipe as AdminPage's AuditLogPanel. */}
        {pagination && pagination.totalPages > 1 && !loading && filteredCases.length > 0 && (
          <div className="flex items-center justify-between mt-5 pt-4 border-t border-casevo-border">
            <button
              type="button"
              onClick={handlePrevPage}
              disabled={page <= 1 || pageChanging}
              className="text-xs uppercase tracking-widest text-casevo-brand hover:text-casevo-mid transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-casevo-brand"
            >
              ← Prev
            </button>
            <span className="text-xs text-casevo-mid">
              Page {pagination.page} of {pagination.totalPages}
              {pagination.total > 0 && <span className="ml-2">({pagination.total} total)</span>}
            </span>
            <button
              type="button"
              onClick={handleNextPage}
              disabled={page >= pagination.totalPages || pageChanging}
              className="text-xs uppercase tracking-widest text-casevo-brand hover:text-casevo-mid transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-casevo-brand"
            >
              Next →
            </button>
          </div>
        )}
      </div>
      {/*
        Keyboard-shortcuts help overlay. Triggered by "?" (handled in
        the global keydown effect above). Closed by Esc / X button /
        backdrop click. Outer flex centers the panel; padding p-4 keeps
        the panel off the screen edges on narrow viewports.
      */}
      {showHelp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowHelp(false)}
            aria-hidden="true"
          />
          <div
            ref={dialogRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-labelledby="help-overlay-title"
            className="relative z-10 bg-casevo-surface rounded-lg shadow-lg p-6 max-w-md w-full focus:outline-none"
          >
            <button
              type="button"
              onClick={() => setShowHelp(false)}
              aria-label="Close help"
              className="absolute right-3 top-3 text-casevo-mid hover:text-casevo-forest transition-colors p-1 rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-light"
            >
              ✕
            </button>
            <h2
              id="help-overlay-title"
              className="text-casevo-forest font-medium text-lg uppercase tracking-widest mb-4 pr-8"
            >
              Keyboard shortcuts
            </h2>
            <ul className="flex flex-col gap-3">
              {[
                { keys: ['/'], label: 'Focus search' },
                { keys: ['Esc'], label: 'Clear search (or close this help)' },
                { keys: ['?'], label: 'Show this help' },
              ].map(({ keys, label }) => (
                <li
                  key={label}
                  className="flex items-center justify-between gap-3 text-sm"
                >
                  <span className="text-casevo-mid">{label}</span>
                  <span className="flex items-center gap-1">
                    {keys.map((k) => (
                      <kbd
                        key={k}
                        className="inline-block border border-casevo-border rounded px-2 py-0.5 bg-casevo-input text-casevo-forest font-mono text-xs"
                      >
                        {k}
                      </kbd>
                    ))}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}
export default CaseListScreen
